import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import ModalRecibo from '../components/ModalRecibo';

const fmt = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const NOMBRES_MES = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];
const ESTADOS_REGISTRO = ['PAGADO','PENDIENTE','FACTURADO'];
const ESTADOS = [...ESTADOS_REGISTRO, 'RETEIVA'];
const _hoy = new Date();
const ANIO_ACTUAL = String(_hoy.getFullYear());
const MES_ACTUAL  = NOMBRES_MES[_hoy.getMonth()];
const ANIOS_INICIALES = [ANIO_ACTUAL];
const RETEIVA_PCT_DEFAULT = 15;

const ESTADO_STYLE = {
  PAGADO:    { bg: 'rgba(0,184,227,0.12)',   color: '#00b8e3' },
  FACTURADO: { bg: 'rgba(99,102,241,0.12)',  color: '#6366f1' },
  PENDIENTE: { bg: 'rgba(251,191,36,0.12)',  color: '#b45309' },
  RETEIVA:   { bg: 'rgba(14,165,233,0.12)',  color: '#0284c7' },
};

const emptyForm = { fecha: new Date().toISOString().slice(0,10), cliente: '', solicitante: '', servicio: '', codigo_dian: '', tipo: 'PARTICULAR', valor: '', domicilio: '', costo: '', comision_pct: '', factura: 'NO', con_iva: 1, reteiva_aplica: 0, reteiva_modo: 'NO', reteiva_pct: RETEIVA_PCT_DEFAULT, reteiva_pago_empresa: '', pago_dividido_aplica: 0, pago_cuotas_total: 2, pago_factura_modo: 'UNICA', numero_factura: '', proveedor: '', estado: 'PENDIENTE', notas: '' };
const inp = { background: '#f4f7fa', border: '1px solid #e8eef2', color: '#08293f' };

function claveCliente(nombre) {
  return String(nombre || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es').trim().replace(/[\s_-]+/g, '');
}

function addMonthsToDate(dateStr, months) {
  const [year, month, day] = String(dateStr || new Date().toISOString().slice(0,10)).split('-').map(Number);
  const target = new Date(year, (month || 1) - 1 + months, 1);
  const maxDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day || 1, maxDay));
  return target.toISOString().slice(0, 10);
}

function Tag({ text }) {
  const s = ESTADO_STYLE[text] || { bg: '#f4f7fa', color: '#5a7a8a' };
  return <span style={{ ...s, padding: '2px 7px', fontSize: 10, fontWeight: 600, letterSpacing: '0.05em' }}>{text}</span>;
}

function RegistroAcciones({ registro, onMarcarPagado, onEditar, onDuplicar, onToggleNoFacturo, onEliminar }) {
  const cerrarMenu = event => event.currentTarget.closest('details')?.removeAttribute('open');

  return (
    <div className="registro-acciones">
      {registro.estado !== 'PAGADO' && (
        <button type="button" className="registro-accion registro-accion-pagar" onClick={() => onMarcarPagado(registro.id)}>
          <span aria-hidden="true">✓</span> Marcar pagado
        </button>
      )}
      <button type="button" className="registro-accion registro-accion-editar" onClick={() => onEditar(registro)}>
        Editar
      </button>
      <details
        className="registro-acciones-menu"
        name="registro-acciones"
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.removeAttribute('open');
        }}
      >
        <summary className="registro-accion registro-accion-mas">••• Más <span aria-hidden="true">⌄</span></summary>
        <div className="registro-acciones-popover">
          <button type="button" onClick={event => { cerrarMenu(event); onDuplicar(registro); }}>
            <span aria-hidden="true">▣</span> Duplicar
          </button>
          <button type="button" onClick={event => { cerrarMenu(event); onToggleNoFacturo(registro.id); }}>
            <span aria-hidden="true">⊘</span> {registro.no_facturo ? 'Incluir en mi facturación' : 'No facturado por mí'}
          </button>
          <div className="registro-acciones-separador" />
          <button type="button" className="registro-accion-eliminar" onClick={event => { cerrarMenu(event); onEliminar(registro.id); }}>
            <span aria-hidden="true">⌫</span> Eliminar
          </button>
        </div>
      </details>
    </div>
  );
}

// Resumen en línea del plan de pago dividido (suma de todas las cuotas, de todos los meses)
function PlanResumen({ r, onVer }) {
  if (r.plan_valor_total == null) return null;
  return (
    <div style={{ fontSize: 10, color: '#5a7a8a', fontWeight: 700, marginTop: 2 }}>
      Plan: total {fmt(r.plan_valor_total)} · utilidad {fmt(r.plan_utilidad_total)} · {(r.plan_cuotas_pagadas || 0)}/{r.plan_cuotas} cobradas
      {' '}
      <button onClick={() => onVer(r.pago_plan_id)} style={{ fontSize: 10, color: '#00b8e3', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 800 }}>ver desglose</button>
    </div>
  );
}

// Modal con el desglose de las cuotas de un plan (todas, incluidos otros meses)
function ModalPlanDesglose({ detalle, onClose }) {
  if (!detalle) return null;
  const cuotas = detalle.cuotas || [];
  const total = cuotas.reduce((s, c) => s + (parseFloat(c.valor) || 0), 0);
  const util = cuotas.reduce((s, c) => s + (parseFloat(c.utilidad) || 0), 0);
  const pagadas = cuotas.filter(c => c.estado === 'PAGADO').length;
  const th = { padding: '6px 8px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', color: '#7f9aad', fontWeight: 800 };
  const td = { padding: '6px 8px', borderTop: '1px solid #eef2f6' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 580, maxHeight: '85vh', overflow: 'auto', padding: 20, borderRadius: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: '#08293f' }}>Plan de pago dividido — {cuotas[0]?.servicio || ''}</h3>
          <button onClick={onClose} style={{ border: '1px solid #d8e5ec', background: '#fff', padding: '4px 10px', cursor: 'pointer' }}>Cerrar</button>
        </div>
        <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
          <div><div style={{ fontSize: 10, color: '#7f9aad', fontWeight: 800, textTransform: 'uppercase' }}>Valor total</div><div style={{ fontSize: 18, fontWeight: 900, color: '#08293f' }}>{fmt(total)}</div></div>
          <div><div style={{ fontSize: 10, color: '#7f9aad', fontWeight: 800, textTransform: 'uppercase' }}>Utilidad total</div><div style={{ fontSize: 18, fontWeight: 900, color: '#059669' }}>{fmt(util)}</div></div>
          <div><div style={{ fontSize: 10, color: '#7f9aad', fontWeight: 800, textTransform: 'uppercase' }}>Cobradas</div><div style={{ fontSize: 18, fontWeight: 900, color: '#6366f1' }}>{pagadas}/{cuotas.length}</div></div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr><th style={th}>Cuota</th><th style={th}>Mes</th><th style={{ ...th, textAlign: 'right' }}>Valor</th><th style={{ ...th, textAlign: 'right' }}>Utilidad</th><th style={th}>Estado</th></tr></thead>
          <tbody>
            {cuotas.map(c => (
              <tr key={c.id}>
                <td style={td}>{c.pago_cuota_num}/{c.pago_cuotas_total}</td>
                <td style={td}>{c.mes}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(c.valor)}</td>
                <td style={{ ...td, textAlign: 'right', color: '#059669' }}>{fmt(c.utilidad)}</td>
                <td style={td}>{c.estado === 'PAGADO' ? '✓ Cobrada' : c.estado}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompactAutocomplete({ value, onChange, options, inputStyle, placeholder, newItemLabel }) {
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const normalize = text => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const optionValue = option => typeof option === 'object' ? option.value : option;
  const optionLabel = option => typeof option === 'object' ? (option.label || option.value) : option;
  const optionSearch = option => typeof option === 'object' ? [option.label, option.value, option.search].filter(Boolean).join(' ') : option;
  const query = normalize(value);
  const matches = query.length >= 3
    ? (options || []).filter(option => normalize(optionSearch(option)).includes(query)).slice(0, 8)
    : [];
  const open = focused && query.length >= 3;

  function select(option) {
    onChange(optionValue(option));
    setFocused(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event) {
    if (!open || !matches.length) {
      if (event.key === 'Escape') setFocused(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => Math.min(index + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      select(matches[activeIndex]);
    } else if (event.key === 'Escape') {
      setFocused(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="input"
        value={value}
        style={inputStyle}
        placeholder={placeholder || 'Escribe mínimo 3 letras'}
        autoComplete="off"
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 120)}
        onKeyDown={handleKeyDown}
        onChange={event => { onChange(event.target.value); setActiveIndex(-1); }}
        required
      />
      {open && (
        <div style={{
          position: 'absolute', zIndex: 80, top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #cfdde8', borderRadius: 3,
          boxShadow: '0 12px 28px rgba(8,41,63,0.18)', overflow: 'hidden',
        }}>
          {matches.map((option, index) => (
            <button
              key={`${optionValue(option)}-${index}`}
              type="button"
              onMouseDown={event => event.preventDefault()}
              onClick={() => select(option)}
              style={{
                display: 'block', width: '100%', padding: '9px 11px', textAlign: 'left',
                background: activeIndex === index ? '#eaf8fc' : '#fff',
                color: '#08293f', border: 'none', borderBottom: '1px solid #edf3f7',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
              }}
            >
              {optionLabel(option)}
            </button>
          ))}
          {!matches.length && (
            <div style={{ padding: '10px 11px', color: '#5a7a8a', fontSize: 11, lineHeight: 1.4 }}>
              Sin coincidencias. Puedes guardar “{value.trim()}” como {newItemLabel || 'elemento nuevo'}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Registros() {
  const [registros, setRegistros] = useState([]);
  const [anios, setAnios] = useState(ANIOS_INICIALES);
  const [listas,    setListas]    = useState({ clientes: [], servicios: [], proveedores: [] });
  const [filtros,   setFiltros]   = useState({ anio: ANIO_ACTUAL, mes: MES_ACTUAL, estado: '', cliente: '' });
  const [form,      setForm]      = useState(emptyForm);
  const [editId,    setEditId]    = useState(null);
  const [duplicando,setDuplicando]= useState(false);
  const [showForm,  setShowForm]  = useState(false);
  const [planDetalle, setPlanDetalle] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [cambioCliente, setCambioCliente] = useState(null); // { editId, de, a, payload } al cambiar el cliente de un registro
  const [reciboAbierto, setReciboAbierto] = useState(false);
  // Un recibo es para lo que NO se factura: sin IVA ni ReteIVA. Un servicio con factura no va en recibo.
  const esFacturado = r => String(r.factura || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase() === 'SI' || (parseFloat(r.valor_iva) || 0) > 0;
  const [msg,       setMsg]       = useState('');
  const [precioRef, setPrecioRef] = useState(null);
  const precioAutoRef = useRef(null);
  const [agrupado,  setAgrupado]  = useState(true);
  const [vistaRegistros, setVistaRegistros] = useState('estados');
  const [clienteSeleccionadoKey, setClienteSeleccionadoKey] = useState(null);
  const [expandidos, setExpandidos] = useState({});
  const [reteivaSeleccionados, setReteivaSeleccionados] = useState(new Set());
  const [showReteivaLote, setShowReteivaLote] = useState(false);
  const [reteivaPagoGrupo, setReteivaPagoGrupo] = useState('');
  const [ordenCol,  setOrdenCol]  = useState('fecha');
  const [ordenDir,   setOrdenDir]  = useState('desc');
  const [refreshKey, setRefreshKey] = useState(0);
  const [modoVal,   setModoVal]   = useState(false);
  const [validados, setValidados] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('val_ids') || '[]')); } catch { return new Set(); }
  });
  const [mesesAuditados,   setMesesAuditados]   = useState(new Set());
  const [selAgrup,         setSelAgrup]         = useState(new Set());
  const [gruposDisponibles,setGruposDisponibles] = useState([]);
  const [destGrupo,        setDestGrupo]        = useState('');
  const [savingAgrup,      setSavingAgrup]      = useState(false);
  const [msgAgrup,         setMsgAgrup]         = useState('');
  const [clienteCastigo,   setClienteCastigo]   = useState(null);

  useEffect(() => {
    API.get('/meses-auditados').then(r => setMesesAuditados(new Set(r.data))).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/clientes/grupos').then(r => setGruposDisponibles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/registros-anios').then(r => {
      const list = Array.isArray(r.data) && r.data.length ? r.data.map(String) : ANIOS_INICIALES;
      setAnios(list);
    }).catch(() => setAnios(ANIOS_INICIALES));
  }, []);

  useEffect(() => {
    if (!modoVal) { setSelAgrup(new Set()); setDestGrupo(''); }
  }, [modoVal]);

  useEffect(() => {
    const nombre = form.cliente?.trim();
    if (!showForm || !nombre || nombre.length < 2) { setClienteCastigo(null); return; }
    const t = setTimeout(() => {
      API.get(`/clientes/${encodeURIComponent(nombre)}/castigos`)
        .then(r => setClienteCastigo(r.data?.count > 0 ? r.data : null))
        .catch(() => setClienteCastigo(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.cliente, showForm]);

  function toggleSelAgrup(nombre) {
    setSelAgrup(s => { const n = new Set(s); n.has(nombre) ? n.delete(nombre) : n.add(nombre); return n; });
  }

  async function guardarAgrupacion() {
    if (!destGrupo.trim() || selAgrup.size === 0) return;
    setSavingAgrup(true);
    try {
      const grupoExistente = gruposDisponibles.find(g => g.nombre.toLowerCase() === destGrupo.trim().toLowerCase());
      if (grupoExistente) {
        for (const cliente of selAgrup)
          await API.post(`/clientes/grupos/${grupoExistente.id}/miembros`, { cliente });
      } else {
        await API.post('/clientes/grupos', { nombre: destGrupo.trim(), nit: '', notas: '', miembros: [...selAgrup] });
      }
      const nombreGrupo = destGrupo.trim();
      const r = await API.get('/clientes/grupos');
      setGruposDisponibles(r.data);
      setSelAgrup(new Set()); setDestGrupo('');
      setMsgAgrup(`✓ Guardado en grupo "${nombreGrupo}"`);
      setTimeout(() => setMsgAgrup(''), 4000);
    } catch(e) { setMsgAgrup('Error: ' + (e.response?.data?.error || e.message)); }
    setSavingAgrup(false);
  }

  const cargarDatos = useCallback(async () => {
    const params = {};
    if (filtros.anio && filtros.mes) params.mes = filtros.mes + ' ' + filtros.anio;
    else if (filtros.anio)           params.anio = filtros.anio;
    if (filtros.estado)  params.estado  = filtros.estado;
    if (filtros.cliente) params.cliente = filtros.cliente;
    const [r, l] = await Promise.all([API.get('/registros', { params }), API.get('/listas')]);
    setRegistros(r.data); setListas(l.data);
  }, [filtros, refreshKey]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  useEffect(() => {
    if (!showForm || !form.servicio.trim()) {
      setPrecioRef(null);
      return;
    }
    let cancelado = false;
    const timer = setTimeout(async () => {
      try {
        const r = await API.get('/servicios/precio-sugerido', {
          params: { servicio: form.servicio, cliente: form.cliente },
        });
        if (cancelado) return;
        const ref = r.data?.encontrado ? r.data : null;
        setPrecioRef(ref);
        if (!editId && ref?.precio_sugerido != null) {
          const valorSugerido = String(Math.round(Number(ref.precio_sugerido)));
          setForm(actual => {
            if (actual.valor !== '' && String(actual.valor) !== String(precioAutoRef.current)) return actual;
            precioAutoRef.current = valorSugerido;
            return { ...actual, valor: valorSugerido };
          });
        }
      } catch {
        if (!cancelado) setPrecioRef(null);
      }
    }, 350);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [showForm, form.servicio, form.cliente, editId]);

  const domicilioCalc = parseFloat(form.domicilio) || 0;
  const valorTotalCalc = (parseFloat(form.valor) || 0) + domicilioCalc;
  const pagoDivididoActivo = !!form.pago_dividido_aplica;
  const pagoCuotasTotal = Math.max(1, parseInt(form.pago_cuotas_total) || 1);
  const pagoCuotaBaseCalc = pagoDivididoActivo && pagoCuotasTotal > 1 ? Math.round(valorTotalCalc / pagoCuotasTotal) : valorTotalCalc;
  const ivaCalc      = (form.factura === 'SI' && form.con_iva) ? Math.round(valorTotalCalc * 0.19) : 0;
  const totalConIvaCalc = valorTotalCalc + ivaCalc;
  const reteivaModo = form.reteiva_modo || (form.reteiva_aplica ? 'PAGO' : 'NO');
  const reteivaPct = parseFloat(form.reteiva_pct) || RETEIVA_PCT_DEFAULT;
  const reteivaPctCalc = Math.round(ivaCalc * reteivaPct / 100);
  const reteivaPagoCalc = parseFloat(form.reteiva_pago_empresa) || 0;
  const reteivaCalc = reteivaModo === 'PCT'
    ? reteivaPctCalc
    : form.reteiva_aplica ? Math.max(0, Math.round(totalConIvaCalc - reteivaPagoCalc)) : 0;
  const comisionCalc = Math.round(valorTotalCalc * (parseFloat(form.comision_pct) || 0) / 100);
  const utilCalc     = valorTotalCalc - (parseFloat(form.costo) || 0) - comisionCalc;

  function prepararRegistroParaGuardar(base) {
    const domicilio = parseFloat(base.domicilio) || 0;
    const valorTotal = (parseFloat(base.valor) || 0) + domicilio;
    const iva = (base.factura === 'SI' && base.con_iva) ? Math.round(valorTotal * 0.19) : 0;
    const totalConIva = valorTotal + iva;
    const modo = base.reteiva_modo || (base.reteiva_aplica ? 'PAGO' : 'NO');
    const { reteiva_modo, reteiva_pct, ...payload } = base;
    if (modo === 'PCT') {
      const pct = parseFloat(reteiva_pct) || RETEIVA_PCT_DEFAULT;
      const reteiva = Math.round(iva * pct / 100);
      return {
        ...payload,
        reteiva_aplica: reteiva > 0 ? 1 : 0,
        reteiva_pago_empresa: reteiva > 0 ? String(Math.max(0, totalConIva - reteiva)) : '',
      };
    }
    if (modo === 'NO') return { ...payload, reteiva_aplica: 0, reteiva_pago_empresa: '' };
    return { ...payload, reteiva_aplica: 1 };
  }

  function prepararCuotasPagoDividido() {
    const cuotas = Math.max(2, parseInt(form.pago_cuotas_total) || 2);
    const planId = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `plan-${Date.now()}`;
    const total = valorTotalCalc;
    const costoTotal = parseFloat(form.costo) || 0;
    const cuotaBase = Math.floor(total / cuotas);
    const costoBase = Math.floor(costoTotal / cuotas);
    let valorAsignado = 0;
    let costoAsignado = 0;
    const modo = form.pago_factura_modo || 'UNICA';
    return Array.from({ length: cuotas }, (_, index) => {
      const num = index + 1;
      const valorCuota = index === cuotas - 1 ? Math.max(0, total - valorAsignado) : cuotaBase;
      const costoCuota = index === cuotas - 1 ? Math.max(0, costoTotal - costoAsignado) : costoBase;
      valorAsignado += valorCuota;
      costoAsignado += costoCuota;
      const facturaBase = modo === 'UNICA'
        ? (index === 0 ? total : 0)
        : valorCuota;
      const payloadBase = {
        ...form,
        fecha: addMonthsToDate(form.fecha, index),
        valor: String(valorCuota),
        domicilio: '',
        costo: String(costoCuota),
        factura: modo === 'UNICA' ? 'SI' : form.factura,
        con_iva: modo === 'UNICA' ? (index === 0 ? form.con_iva : 0) : form.con_iva,
        factura_base_ss: facturaBase,
        pago_dividido_aplica: 1,
        pago_plan_id: planId,
        pago_cuota_num: num,
        pago_cuotas_total: cuotas,
        pago_factura_modo: modo,
        pago_factura_estado: modo === 'UNICA' ? (index === 0 ? 'FACTURA_TOTAL' : 'YA_FACTURADO') : 'POR_FACTURAR',
        notas: [form.notas || '', `Pago dividido ${num}/${cuotas} · ${modo === 'UNICA' ? (index === 0 ? 'factura total' : 'ya facturado') : 'factura del mes'}`].filter(Boolean).join('\n'),
      };
      return prepararRegistroParaGuardar(payloadBase);
    });
  }

  async function verPlan(planId) {
    if (!planId) return;
    try {
      const r = await API.get(`/registros/plan/${planId}`);
      setPlanDetalle({ planId, cuotas: r.data });
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  }

  async function guardar(e) {
    e.preventDefault(); setLoading(true);
    try {
      // Pago dividido desde cualquier camino (crear, duplicar, editar), salvo que
      // el registro ya sea parte de un plan (pago_plan_id) para no re-dividir.
      if (pagoDivididoActivo && pagoCuotasTotal > 1 && !form.pago_plan_id) {
        const cuotas = prepararCuotasPagoDividido();
        if (editId) {
          // El registro editado se convierte en la cuota 1; las demás se crean.
          await API.put(`/registros/${editId}`, cuotas[0]);
          for (const cuota of cuotas.slice(1)) await API.post('/registros', cuota);
        } else {
          for (const cuota of cuotas) await API.post('/registros', cuota);
        }
        setShowForm(false); setEditId(null); setDuplicando(false); setForm(emptyForm);
        setRefreshKey(k => k + 1);
        setMsg(`Plan de pago creado: ${cuotas.length} cobros`);
        setTimeout(() => setMsg(''), 3000);
        return;
      }
      const payload = prepararRegistroParaGuardar(form);
      if (editId) {
        const clienteOriginal = registros.find(r => r.id === editId)?.cliente;
        // Si cambió el cliente, NO se renombra nada sin preguntar: puede ser que la persona
        // cambió de empresa (solo este registro) o que se corrige el nombre de todo el cliente.
        if (clienteOriginal && String(form.cliente || '').trim() !== String(clienteOriginal).trim()) {
          setCambioCliente({ editId, de: clienteOriginal, a: String(form.cliente || '').trim(), payload });
          setLoading(false);
          return;
        }
        await API.put(`/registros/${editId}`, payload);
      } else {
        await API.post('/registros', payload);
      }
      setShowForm(false); setEditId(null); setDuplicando(false); setForm(emptyForm);
      setRefreshKey(k => k + 1);
      setMsg(editId ? 'Registro actualizado' : duplicando ? 'Registro duplicado' : 'Registro guardado');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }

  async function confirmarCambioCliente(modo) {
    const c = cambioCliente;
    if (!c) return;
    setCambioCliente(null);
    setLoading(true);
    try {
      await API.put(`/registros/${c.editId}`, c.payload);
      if (modo === 'todos') await API.post('/clientes/renombrar', { nombre_actual: c.de, nombre_nuevo: c.a });
      setShowForm(false); setEditId(null); setDuplicando(false); setForm(emptyForm);
      setRefreshKey(k => k + 1);
      setMsg(modo === 'todos' ? `Cliente renombrado a ${c.a} en todos sus registros` : `Registro movido a ${c.a}`);
      setTimeout(() => setMsg(''), 4000);
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  }

  function formDesdeRegistro(r, overrides = {}) {
    const ivaRegistro = parseFloat(r.valor_iva) || 0;
    const reteivaRegistro = parseFloat(r.reteiva_valor) || 0;
    const reteivaPorPct = reteivaRegistro > 0 && Math.abs(Math.round(ivaRegistro * RETEIVA_PCT_DEFAULT / 100) - Math.round(reteivaRegistro)) <= 1;
    return { fecha: r.fecha?.slice(0,10) || emptyForm.fecha, cliente: r.cliente, solicitante: r.solicitante || '',
      servicio: r.servicio || '', codigo_dian: r.codigo_dian || '', tipo: r.tipo || 'PARTICULAR',
      valor: r.valor, costo: r.costo, comision_pct: r.comision_pct || '', factura: (r.factura === 'SI' || r.factura === 'SÍ') ? 'SI' : 'NO',
      con_iva: r.con_iva ?? 1, reteiva_aplica: r.reteiva_aplica ? 1 : 0, reteiva_modo: r.reteiva_aplica ? (reteivaPorPct ? 'PCT' : 'PAGO') : 'NO', reteiva_pct: RETEIVA_PCT_DEFAULT, reteiva_pago_empresa: r.reteiva_pago_empresa ? String(Math.round(parseFloat(r.reteiva_pago_empresa) || 0)) : '',
      pago_dividido_aplica: r.pago_dividido_aplica ? 1 : 0, pago_cuotas_total: r.pago_cuotas_total || 2, pago_factura_modo: r.pago_factura_modo || 'UNICA',
      factura_base_ss: r.factura_base_ss || '',
      pago_plan_id: r.pago_plan_id || null, pago_cuota_num: r.pago_cuota_num || null, pago_factura_estado: r.pago_factura_estado || null,
      numero_factura: r.numero_factura || '', proveedor: r.proveedor || '', estado: r.estado, notas: r.notas || '', no_facturo: r.no_facturo ? 1 : 0, ...overrides };
  }

  function formActualizacionDesdeRegistro(r, overrides = {}) {
    const domicilio = parseFloat(r.domicilio) || 0;
    return {
      ...formDesdeRegistro(r),
      valor: Math.max(0, (parseFloat(r.valor) || 0) - domicilio) || '',
      domicilio: domicilio || '',
      ...overrides,
    };
  }

  function editar(r) {
    precioAutoRef.current = null;
    setPrecioRef(null);
    const domicilio = parseFloat(r.domicilio) || 0;
    setForm({ ...formDesdeRegistro(r), valor: Math.max(0, (parseFloat(r.valor) || 0) - domicilio) || '', domicilio: domicilio || '' });
    setDuplicando(false);
    setEditId(r.id); setShowForm(true);
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este registro?')) return;
    await API.delete(`/registros/${id}`); cargarDatos();
  }

  function duplicar(r) {
    precioAutoRef.current = null;
    setPrecioRef(null);
    const domicilio = parseFloat(r.domicilio) || 0;
    setForm({ ...formDesdeRegistro(r, { numero_factura: '', estado: 'PENDIENTE', pago_dividido_aplica: 0, pago_plan_id: null, pago_cuota_num: null, pago_factura_estado: null, factura_base_ss: '' }), valor: Math.max(0, (parseFloat(r.valor) || 0) - domicilio) || '', domicilio: domicilio || '' });
    setEditId(null);
    setDuplicando(true);
    setShowForm(true);
  }

  async function marcarPagado(id) {
    await API.patch(`/registros/${id}/estado`, { estado: 'PAGADO' });
    cargarDatos();
  }

  async function toggleNoFacturo(id) {
    await API.patch(`/registros/${id}/no-facturo`);
    cargarDatos();
  }

  function toggleReteivaSeleccion(id) {
    setReteivaSeleccionados(actual => {
      const siguiente = new Set(actual);
      siguiente.has(id) ? siguiente.delete(id) : siguiente.add(id);
      return siguiente;
    });
  }

  async function aplicarReteivaGrupo() {
    if (!serviciosReteivaSeleccionados.length) {
      setMsg('Selecciona los servicios a los que aplica el ReteIVA');
      setTimeout(() => setMsg(''), 3000);
      return;
    }
    if (!reteivaPagoGrupo || pagoReteivaGrupo <= 0) {
      setMsg('Ingresa el valor pagado por la empresa');
      setTimeout(() => setMsg(''), 3000);
      return;
    }
    setLoading(true);
    try {
      let asignado = 0;
      const total = totalReteivaGrupo || 1;
      for (let i = 0; i < serviciosReteivaSeleccionados.length; i += 1) {
        const r = serviciosReteivaSeleccionados[i];
        const totalServicio = (parseFloat(r.valor) || 0) + (parseFloat(r.valor_iva) || 0);
        const reteivaServicio = i === serviciosReteivaSeleccionados.length - 1
          ? Math.max(0, reteivaGrupoCalc - asignado)
          : Math.round(reteivaGrupoCalc * (totalServicio / total));
        asignado += reteivaServicio;
        const pagoEmpresaServicio = Math.max(0, Math.round(totalServicio - reteivaServicio));
        await API.put(`/registros/${r.id}`, formActualizacionDesdeRegistro(r, {
          reteiva_aplica: reteivaServicio > 0 ? 1 : 0,
          reteiva_pago_empresa: reteivaServicio > 0 ? String(pagoEmpresaServicio) : '',
        }));
      }
      setShowReteivaLote(false);
      setReteivaPagoGrupo('');
      setReteivaSeleccionados(new Set());
      setRefreshKey(k => k + 1);
      setMsg(`ReteIVA aplicado: ${fmt(reteivaGrupoCalc)}`);
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }

  const facturados     = registros.filter(r => !r.no_facturo);
  const noFacturados   = registros.filter(r => r.no_facturo);
  const totalBruto     = registros.reduce((s, r) => s + (parseFloat(r.valor)    || 0), 0);
  const totalValor     = facturados.reduce((s, r) => s + (parseFloat(r.valor)    || 0), 0);
  const totalUtilidad  = registros.reduce((s, r) => s + (parseFloat(r.utilidad) || 0), 0);
  const totalUtilidadPropia = facturados.reduce((s, r) => s + (parseFloat(r.utilidad) || 0), 0);
  const totalUtilidadNoFacturo = noFacturados.reduce((s, r) => s + (parseFloat(r.utilidad) || 0), 0);
  const totalIva       = facturados.reduce((s, r) => s + (parseFloat(r.valor_iva) || 0), 0);
  const totalReteiva   = registros.reduce((s, r) => s + (parseFloat(r.reteiva_valor) || 0), 0);
  const totalCosto     = registros.reduce((s, r) => s + (parseFloat(r.costo)    || 0), 0);
  const totalNoFacturo  = noFacturados.reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
  const totalSinFactura = registros.filter(r => !r.no_facturo && r.factura === 'NO').reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
  const totalComisionPagada   = registros.reduce((s, r) => s + Math.round((parseFloat(r.valor) || 0) * (parseFloat(r.comision_pct) || 0) / 100), 0);
  const totalComisionRecibida = registros.filter(r => parseFloat(r.comision_pct) > 0).reduce((s, r) => s + (parseFloat(r.utilidad) || 0), 0);
  const totalPagado    = registros.filter(r => r.estado === 'PAGADO').reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
  const totalFacturado = registros.filter(r => r.estado === 'FACTURADO').reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
  const totalPendiente = registros.filter(r => r.estado === 'PENDIENTE').reduce((s, r) => s + (parseFloat(r.valor) || 0), 0);
  const margenPeriodo  = totalBruto > 0 ? Math.round((totalUtilidad / totalBruto) * 100) : 0;
  const clienteAGrupo = {};
  const clienteAGrupoNormalizado = {};
  gruposDisponibles.forEach(grupo => {
    [grupo.nombre, ...grupo.miembros].forEach(alias => {
      clienteAGrupo[alias] = grupo.nombre;
      const clave = claveCliente(alias);
      if (!(clave in clienteAGrupoNormalizado)) clienteAGrupoNormalizado[clave] = grupo.nombre;
      else if (clienteAGrupoNormalizado[clave] !== grupo.nombre) clienteAGrupoNormalizado[clave] = null;
    });
  });
  const principalCliente = nombre => clienteAGrupo[nombre] || clienteAGrupoNormalizado[claveCliente(nombre)] || nombre;
  const nombreCliente = registro => registro.cliente_organizado || principalCliente(registro.cliente);
  const nombreServicio = registro => registro.servicio_organizado || registro.servicio;
  const clientesFormulario = (() => {
    const opciones = new Map();
    gruposDisponibles.forEach(grupo => opciones.set(grupo.nombre, {
      value: grupo.nombre,
      label: grupo.nombre,
      search: [grupo.nombre, ...grupo.miembros].filter(Boolean).join(' || '),
    }));
    listas.clientes.forEach(cliente => {
      const principal = principalCliente(cliente);
      if (!principal) return;
      if (!opciones.has(principal)) {
        opciones.set(principal, { value: principal, label: principal, search: cliente });
      } else {
        const opcion = opciones.get(principal);
        opcion.search = [opcion.search, cliente].filter(Boolean).join(' || ');
      }
    });
    return [...opciones.values()].sort((a, b) => a.value.localeCompare(b.value, 'es'));
  })();
  const periodoLabel   = filtros.mes && filtros.anio ? `${filtros.mes} ${filtros.anio}` : filtros.anio ? `Año ${filtros.anio}` : 'Todo el histórico';
  const ordenarRegistros = (a, b) => {
    let va, vb;
    if (ordenCol === 'fecha') { va = String(a.fecha || '').slice(0, 10); vb = String(b.fecha || '').slice(0, 10); }
    else if (ordenCol === 'valor') { va = parseFloat(a.valor) || 0; vb = parseFloat(b.valor) || 0; }
    else if (ordenCol === 'estado') { va = a.estado || ''; vb = b.estado || ''; }
    else { va = nombreCliente(a) || ''; vb = nombreCliente(b) || ''; }
    if (typeof va === 'string') return ordenDir === 'asc' ? va.localeCompare(vb, 'es') : vb.localeCompare(va, 'es');
    return ordenDir === 'asc' ? va - vb : vb - va;
  };
  const registrosPorEstado = ESTADOS.map(estado => ({
    estado,
    total: registros.filter(r => estado === 'RETEIVA' ? (parseFloat(r.reteiva_valor) || 0) > 0 : r.estado === estado).reduce((s, r) => s + (estado === 'RETEIVA' ? (parseFloat(r.reteiva_valor) || 0) : (parseFloat(r.valor) || 0)), 0),
    items: registros.filter(r => estado === 'RETEIVA' ? (parseFloat(r.reteiva_valor) || 0) > 0 : r.estado === estado).sort(ordenarRegistros),
  }));

  // ── Validación ────────────────────────────────────────────────
  const mesKey = filtros.mes && filtros.anio ? `${filtros.mes} ${filtros.anio}` : null;
  const mesAuditado = mesKey ? mesesAuditados.has(mesKey) : false;
  const puedeAuditar = (() => {
    if (!filtros.mes || !filtros.anio) return false;
    const mesIdx  = NOMBRES_MES.indexOf(filtros.mes);
    const anioNum = parseInt(filtros.anio);
    if (anioNum < _hoy.getFullYear()) return true;
    if (anioNum === _hoy.getFullYear() && mesIdx <= _hoy.getMonth()) return true;
    return false;
  })();
  const anioAuditadoCheck = (anio) => {
    const anioNum = parseInt(anio);
    const lastIdx = anioNum < _hoy.getFullYear() ? 11 : _hoy.getMonth();
    for (let i = 0; i <= lastIdx; i++) {
      if (!mesesAuditados.has(`${NOMBRES_MES[i]} ${anio}`)) return false;
    }
    return true;
  };
  // IDs visibles en la vista actual
  const idsVista = new Set(registros.map(r => r.id));
  const validadosEnVista = modoVal ? [...validados].filter(id => idsVista.has(id)).length : 0;

  function toggleValidar(id) {
    setValidados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      localStorage.setItem('val_ids', JSON.stringify([...next]));
      return next;
    });
  }
  async function marcarMesAuditado() {
    if (!mesKey) return;
    await API.post('/meses-auditados', { mes_key: mesKey });
    setMesesAuditados(prev => new Set([...prev, mesKey]));
    setValidados(prev => new Set([...prev].filter(id => !idsVista.has(id))));
    setModoVal(false);
  }
  async function desmarcarMesAuditado() {
    if (!mesKey) return;
    await API.delete(`/meses-auditados/${encodeURIComponent(mesKey)}`);
    setMesesAuditados(prev => { const next = new Set(prev); next.delete(mesKey); return next; });
  }

  function toggleOrden(col) {
    if (ordenCol === col) setOrdenDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setOrdenCol(col); setOrdenDir('asc'); }
  }
  function toggleExpandido(cli) { setExpandidos(p => ({ ...p, [cli]: !p[cli] })); }

  const registrosOrdenados = [...registros].sort(ordenarRegistros);

  const clientesIntegrados = (() => {
    const map = {};
    registrosOrdenados.forEach(r => {
      const key = nombreCliente(r);
      if (!map[key]) map[key] = { key, cliente: key, items: [], totalValor: 0, totalUtilidad: 0, totalIva: 0, totalReteiva: 0, totalFacturadoSS: 0, totalCosto: 0, totalPendiente: 0, _nombres: new Set() };
      const e = map[key];
      e.items.push(r);
      e._nombres.add(r.cliente);
      e.totalValor += parseFloat(r.valor) || 0;
      e.totalUtilidad += parseFloat(r.utilidad) || 0;
      e.totalIva += parseFloat(r.valor_iva) || 0;
      e.totalReteiva += parseFloat(r.reteiva_valor) || 0;
      e.totalFacturadoSS += r.pago_factura_estado === 'YA_FACTURADO'
        ? 0
        : (parseFloat(r.factura_base_ss) || ((r.factura === 'SI' || r.factura === 'SÍ') ? (parseFloat(r.valor) || 0) : 0));
      e.totalCosto += parseFloat(r.costo) || 0;
      if (['PENDIENTE','FACTURADO'].includes(r.estado)) e.totalPendiente += parseFloat(r.valor) || 0;
    });
    return Object.values(map)
      .map(e => ({
        ...e,
        esGrupoEmpresa: e._nombres.size > 1 || (e._nombres.size === 1 && !e._nombres.has(e.key)),
        ultimaFecha: e.items.map(r => r.fecha).filter(Boolean).sort().pop() || null,
      }))
      .sort((a, b) => b.totalValor - a.totalValor || a.cliente.localeCompare(b.cliente, 'es'));
  })();
  const clienteResumenSeleccionado = clientesIntegrados.find(c => c.key === clienteSeleccionadoKey) || clientesIntegrados[0] || null;
  const serviciosReteivaSeleccionados = clienteResumenSeleccionado?.items.filter(r => reteivaSeleccionados.has(r.id)) || [];
  const totalReteivaGrupo = serviciosReteivaSeleccionados.reduce((s, r) => s + (parseFloat(r.valor) || 0) + (parseFloat(r.valor_iva) || 0), 0);
  const pagoReteivaGrupo = parseFloat(reteivaPagoGrupo) || 0;
  const reteivaGrupoCalc = Math.max(0, Math.round(totalReteivaGrupo - pagoReteivaGrupo));

  useEffect(() => {
    setReteivaSeleccionados(new Set());
    setShowReteivaLote(false);
    setReteivaPagoGrupo('');
  }, [clienteResumenSeleccionado?.key]);

  // Grupos: clientes con >1 item agrupados individualmente; con 1 item → meta-grupo "Servicios individuales"
  const grupos = (() => {
    const map = {};
    registrosOrdenados.forEach(r => {
      const key = nombreCliente(r);
      if (!map[key]) map[key] = { items: [], totalValor: 0, totalUtilidad: 0, totalIva: 0, esGrupoEmpresa: false, _nombres: new Set() };
      map[key].items.push(r);
      map[key]._nombres.add(r.cliente);
      map[key].totalValor += parseFloat(r.valor) || 0;
      if (!r.no_facturo) {
        map[key].totalUtilidad += parseFloat(r.utilidad)  || 0;
        map[key].totalIva      += parseFloat(r.valor_iva) || 0;
      }
    });
    // Solo marcar EMPRESA cuando hay más de un nombre de cliente distinto en el grupo
    Object.entries(map).forEach(([key, g]) => {
      g.esGrupoEmpresa = g._nombres.size > 1 || (g._nombres.size === 1 && !g._nombres.has(key));
    });
    const sortFn = (a, b) => {
      if (ordenCol === 'valor')   return ordenDir === 'asc' ? a[1].totalValor - b[1].totalValor : b[1].totalValor - a[1].totalValor;
      if (ordenCol === 'cliente') return ordenDir === 'asc' ? a[0].localeCompare(b[0]) : b[0].localeCompare(a[0]);
      return b[1].totalValor - a[1].totalValor;
    };
    const multi   = Object.entries(map).filter(([, g]) => g.items.length > 1).sort(sortFn);
    const singles = Object.entries(map).filter(([, g]) => g.items.length === 1).sort(sortFn);
    const result  = [...multi];
    if (singles.length > 0) {
      result.push(['__singles__', {
        isSingles: true, clients: singles,
        items: singles.flatMap(([, g]) => g.items),
        totalValor:    singles.reduce((s, [, g]) => s + g.totalValor,    0),
        totalUtilidad: singles.reduce((s, [, g]) => s + g.totalUtilidad, 0),
        totalIva:      singles.reduce((s, [, g]) => s + g.totalIva,      0),
      }]);
    }
    return result;
  })();

  function SortTh({ col, label, style }) {
    const active = ordenCol === col;
    return (
      <th style={{ cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => toggleOrden(col)}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          {label}
          <span style={{ fontSize: 8, color: active ? '#00b8e3' : '#c8d0d6', lineHeight: 1 }}>
            {active ? (ordenDir === 'asc' ? '▲' : '▼') : '◇'}
          </span>
        </span>
      </th>
    );
  }

  return (
    <div className="registros-page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#08293f', margin: 0 }}>Registros</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={() => { precioAutoRef.current = null; setPrecioRef(null); setForm(emptyForm); setEditId(null); setDuplicando(false); setShowForm(true); }}>+ Nuevo</button>
        </div>
      </div>

      {msg && (
        <div style={{ marginBottom: 14, padding: '10px 14px', fontSize: 13, fontWeight: 600,
          background: msg.startsWith('Error') ? 'rgba(248,113,113,0.1)' : 'rgba(0,184,227,0.1)',
          border: `1px solid ${msg.startsWith('Error') ? 'rgba(248,113,113,0.3)' : 'rgba(0,184,227,0.3)'}`,
          color: msg.startsWith('Error') ? '#dc2626' : '#00b8e3' }}>
          {msg}
        </div>
      )}

      {/* Dashboard */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 8, marginBottom: 8 }} className="registros-kpi-grid">
        {[
          { label: 'Ingresos registrados', value: fmt(totalBruto), detail: `${registros.length} registros`, dark: true },
          { label: 'Utilidad bruta', value: fmt(totalUtilidad), detail: 'incluye comisiones', color: '#059669' },
          { label: 'Facturado', value: fmt(totalValor), detail: 'base SS sin excluidos', color: '#08293f' },
          { label: 'Por cobrar', value: fmt(totalFacturado + totalPendiente), detail: 'facturado + pendiente', color: '#dc2626' },
          { label: 'Margen', value: `${margenPeriodo}%`, detail: periodoLabel, color: '#6366f1' },
        ].map(k => (
          <div className={`registro-kpi-card${k.dark ? ' registro-kpi-card-principal' : ''}`} key={k.label} style={{ background: k.dark ? '#08293f' : '#fff', border: k.dark ? '1px solid rgba(0,184,227,0.22)' : '1px solid #e8eef2', borderRadius: 2, padding: '13px 15px', minHeight: 86 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: k.dark ? 'rgba(255,255,255,0.62)' : '#5a7a8a', marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 20, lineHeight: 1, fontWeight: 900, color: k.dark ? '#00b8e3' : (k.color || '#08293f'), whiteSpace: 'nowrap' }}>{k.value}</div>
            <div style={{ fontSize: 11, color: k.dark ? 'rgba(255,255,255,0.68)' : '#5a7a8a', marginTop: 7 }}>{k.detail}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8, marginBottom: 12 }} className="registros-subkpi-grid">
        {[
          { label: 'Pagado', value: fmt(totalPagado), color: '#059669' },
          { label: 'Pendiente', value: fmt(totalPendiente), color: '#b45309' },
          { label: 'Facturado', value: fmt(totalFacturado), color: '#6366f1' },
          { label: 'ReteIVA', value: fmt(totalReteiva), color: '#0284c7', mobileHidden: true },
        ].map(k => (
          <div className={`registro-subkpi-card${k.mobileHidden ? ' registro-subkpi-reteiva' : ''}`} key={k.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '9px 12px', minHeight: 44 }}>
            <span style={{ color: '#5a7a8a', fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{k.label}</span>
            <b style={{ color: k.color, fontSize: 13, fontWeight: 900, whiteSpace: 'nowrap' }}>{k.value}</b>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="filtros-grid">
          <div>
            <label className="label">Año</label>
            <select className="input" value={filtros.anio} onChange={e => setFiltros(f => ({ ...f, anio: e.target.value, mes: '' }))}>
              <option value="">Todo</option>
              {anios.map(a => <option key={a} value={a}>{a}{anioAuditadoCheck(a) ? ' ✓' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Mes</label>
            <select className="input" value={filtros.mes} onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))} disabled={!filtros.anio}>
              <option value="">Todo el año</option>
              {NOMBRES_MES.map(m => {
                const mk = `${m} ${filtros.anio}`;
                return <option key={m} value={m}>{m}{mesesAuditados.has(mk) ? ' ✓' : ''}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="label">Estado</label>
            <select className="input" value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))}>
              <option value="">Todos</option>
              {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Cliente</label>
            <input className="input" placeholder="Buscar..." value={filtros.cliente}
              onChange={e => setFiltros(f => ({ ...f, cliente: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn-secondary" style={{ width: '100%' }} onClick={() => setFiltros({ anio: ANIO_ACTUAL, mes: MES_ACTUAL, estado: '', cliente: '' })}>Limpiar</button>
          </div>
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f4f7fa', display: 'none', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#5a7a8a' }}><strong style={{ color: '#08293f' }}>{registros.length}</strong> registros</span>
          {[
            { label: 'Ingreso bruto', value: fmt(totalBruto),            color: '#00b8e3' },
            { label: 'Ingreso + IVA', value: fmt(totalValor + totalIva), color: '#6366f1' },
            { label: 'Costo',         value: fmt(totalCosto),            color: '#5a7a8a' },
            { label: 'Utilidad total', value: fmt(totalUtilidad),        color: '#059669' },
            { label: 'IVA',           value: fmt(totalIva),              color: '#b45309' },
          ].map((k, i) => (
            <div key={i}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#5a7a8a', marginBottom: 1 }}>{k.label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: k.color }}>{k.value}</div>
            </div>
          ))}
          <div style={{ padding: '4px 10px', background: 'rgba(180,83,9,0.07)', borderRadius: 2, border: '1px solid rgba(180,83,9,0.2)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#b45309', marginBottom: 1 }}>No facturado por mí</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>− {fmt(totalNoFacturo)}</div>
          </div>
          <div style={{ padding: '4px 10px', background: 'rgba(5,150,105,0.04)', borderRadius: 2, border: '1px solid rgba(5,150,105,0.16)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#059669', marginBottom: 1 }}>Utilidad propia</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{fmt(totalUtilidadPropia)}</div>
          </div>
          {totalUtilidadNoFacturo > 0 && (
            <div style={{ padding: '4px 10px', background: 'rgba(5,150,105,0.06)', borderRadius: 2, border: '1px solid rgba(5,150,105,0.2)' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#059669', marginBottom: 1 }}>Utilidad no facturo</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{fmt(totalUtilidadNoFacturo)}</div>
            </div>
          )}
          <div style={{ padding: '4px 10px', background: 'rgba(180,83,9,0.07)', borderRadius: 2, border: '1px solid rgba(180,83,9,0.2)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#b45309', marginBottom: 1 }}>Sin factura</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>− {fmt(totalSinFactura)}</div>
          </div>
          <div style={{ padding: '4px 10px', background: 'rgba(0,184,227,0.07)', borderRadius: 2, border: '1px solid rgba(0,184,227,0.25)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#0284c7', marginBottom: 1 }}>Ingreso bruto SS</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0284c7' }}>{fmt(totalBruto - totalNoFacturo - totalSinFactura)}</div>
          </div>
          <div style={{ padding: '4px 10px', background: 'rgba(220,38,38,0.06)', borderRadius: 2, border: '1px solid rgba(220,38,38,0.2)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#dc2626', marginBottom: 1 }}>Comisión pagada</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>− {fmt(totalComisionPagada)}</div>
          </div>
          <div style={{ padding: '4px 10px', background: 'rgba(5,150,105,0.06)', borderRadius: 2, border: '1px solid rgba(5,150,105,0.2)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#059669', marginBottom: 1 }}>Comisión recibida</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{fmt(totalComisionRecibida)}</div>
          </div>
          {/* Botón modo validación */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {mesAuditado && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#059669', background: 'rgba(5,150,105,0.1)', border: '1px solid rgba(5,150,105,0.25)', padding: '3px 10px', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
                ✓ Mes auditado
                <button onClick={desmarcarMesAuditado} style={{ fontSize: 9, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }} title="Desmarcar">×</button>
              </span>
            )}
            {puedeAuditar && !mesAuditado && (
              <button onClick={() => setModoVal(v => !v)} style={{
                fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 2, cursor: 'pointer', fontFamily: 'inherit',
                background: modoVal ? 'rgba(99,102,241,0.12)' : '#fff',
                color: modoVal ? '#6366f1' : '#5a7a8a',
                border: `1px solid ${modoVal ? '#6366f1' : '#e8eef2'}`
              }}>
                {modoVal ? '🔍 Validando...' : '🔍 Modo validación'}
              </button>
            )}
          </div>
        </div>
        {/* Banner validación activo */}
        {modoVal && puedeAuditar && (
          <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 700 }}>Modo validación activo</span>
            <span style={{ fontSize: 11, color: '#5a7a8a' }}>
              <strong style={{ color: '#6366f1' }}>{validadosEnVista}</strong> / {registros.length} validados
            </span>
            <div style={{ flex: 1, height: 4, background: '#e8eef2', borderRadius: 2, minWidth: 80 }}>
              <div style={{ height: 4, background: '#6366f1', borderRadius: 2, width: `${registros.length > 0 ? Math.round(validadosEnVista / registros.length * 100) : 0}%`, transition: 'width 0.3s' }} />
            </div>
            {mesKey && (
              <button onClick={marcarMesAuditado} style={{ fontSize: 11, fontWeight: 700, padding: '5px 14px', borderRadius: 2, cursor: 'pointer', fontFamily: 'inherit',
                background: '#059669', color: '#fff', border: 'none' }}>
                ✓ Marcar {filtros.mes} como auditado
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 680 }}>
            <div style={{ padding: '24px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 13, fontWeight: 700, color: '#08293f', margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    {duplicando ? 'Duplicar registro' : editId ? 'Editar registro' : 'Nuevo registro'}
                  </h2>
                  <div style={{ width: 28, height: 2, background: '#00b8e3', marginTop: 5 }} />
                </div>
                <button onClick={() => { setShowForm(false); setEditId(null); setDuplicando(false); }}
                  style={{ fontSize: 22, fontWeight: 300, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
              </div>

              <form onSubmit={guardar}>
                <div className="form-grid2" style={{ marginBottom: 12 }}>
                  <div><label className="label">Fecha *</label>
                    <input className="input" type="date" value={form.fecha} style={inp}
                      onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} required /></div>
                  <div><label className="label">Tipo</label>
                    <select className="input" value={form.tipo} style={inp} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                      <option value="PARTICULAR">PARTICULAR</option>
                      <option value="FIJO">FIJO</option>
                    </select></div>
                </div>

                <div className="form-grid2" style={{ marginBottom: 12 }}>
                  <div><label className="label">Cliente *</label>
                    <CompactAutocomplete
                      value={form.cliente}
                      options={clientesFormulario}
                      inputStyle={inp}
                      newItemLabel="cliente nuevo"
                      onChange={cliente => setForm(f => ({ ...f, cliente }))}
                    /></div>
                  <div><label className="label">Solicitante <span style={{ color: '#b3b3b3', fontWeight: 400 }}>(opcional)</span></label>
                    <input className="input" value={form.solicitante} style={inp} placeholder="Nombre de quien solicita"
                      onChange={e => setForm(f => ({ ...f, solicitante: e.target.value }))} /></div>
                </div>

                {clienteCastigo && (
                  <div style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.35)', borderRadius: 2, padding: '10px 12px', marginBottom: 12, fontSize: 11 }}>
                    <div style={{ fontWeight: 800, color: '#dc2626', marginBottom: 4 }}>
                      ⚠️ Este cliente tiene cartera castigada — {clienteCastigo.count} servicio(s) por {fmt(clienteCastigo.total)}
                    </div>
                    <div style={{ color: '#9a3a3a' }}>
                      {clienteCastigo.items.slice(0, 3).map(c => `${c.servicio} (${fmt(c.valor)}, ${c.fecha?.slice(0,10)})`).join(' · ')}
                      {clienteCastigo.items.length > 3 ? ` y ${clienteCastigo.items.length - 3} más` : ''}
                    </div>
                  </div>
                )}

                <div className="form-grid2" style={{ marginBottom: 12 }}>
                  <div><label className="label">Proveedor <span style={{ color: '#b3b3b3', fontWeight: 400 }}>(quien prestó / vendió)</span></label>
                    <CompactAutocomplete
                      value={form.proveedor}
                      options={listas.proveedores || []}
                      inputStyle={inp}
                      placeholder="Ej: Samsung, Garantia Lenovo..."
                      newItemLabel="proveedor nuevo"
                      onChange={proveedor => setForm(f => ({ ...f, proveedor }))}
                    /></div>
                </div>
                <div className="form-grid2" style={{ marginBottom: 12 }}>
                  <div><label className="label">Servicio</label>
                    <CompactAutocomplete
                      value={form.servicio}
                      options={listas.servicios_busqueda || listas.servicios}
                      inputStyle={inp}
                      newItemLabel="servicio nuevo"
                      onChange={servicio => setForm(f => ({ ...f, servicio }))}
                    /></div>
                  <div><label className="label">Código DIAN <span style={{ color: '#b3b3b3', fontWeight: 400 }}>(opcional)</span></label>
                    <input className="input" value={form.codigo_dian} style={inp} placeholder="Ej: 7301"
                      onChange={e => setForm(f => ({ ...f, codigo_dian: e.target.value }))} /></div>
                </div>

                {precioRef && (
                  <div style={{ margin: '-3px 0 12px', padding: '9px 11px', background: '#eefaf5', border: '1px solid #cde9dd', color: '#315f50', fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div>
                        <b style={{ color: '#047857' }}>
                          Sugerido: {precioRef.precio_sugerido != null ? fmt(precioRef.precio_sugerido) : 'sin valor definido'}
                        </b>
                        <span style={{ marginLeft: 6 }}>
                          {precioRef.origen === 'cliente' ? `precio para ${precioRef.cliente_precio || form.cliente}` :
                            precioRef.origen === 'base' ? 'precio base del servicio' :
                            precioRef.origen === 'historico' ? 'promedio histórico del cliente' : ''}
                        </span>
                      </div>
                      {precioRef.precio_sugerido != null && String(Math.round(Number(precioRef.precio_sugerido))) !== String(form.valor) && (
                        <button type="button" onClick={() => {
                          const valor = String(Math.round(Number(precioRef.precio_sugerido)));
                          precioAutoRef.current = valor;
                          setForm(f => ({ ...f, valor }));
                        }}
                          style={{ border: '1px solid #9fd3bd', background: '#fff', color: '#047857', padding: '5px 8px', cursor: 'pointer', fontWeight: 800, fontSize: 10 }}>
                          Usar sugerido
                        </button>
                      )}
                    </div>
                    {precioRef.historico?.registros > 0 && (
                      <div style={{ color: '#6b8295', marginTop: 4 }}>
                        Histórico relacionado: {precioRef.historico.registros} registro(s) · promedio {fmt(precioRef.historico.promedio)}
                        {precioRef.historico.minimo != null && ` · rango ${fmt(precioRef.historico.minimo)} a ${fmt(precioRef.historico.maximo)}`}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 12 }}>
                  <div><label className="label">Valor *</label>
                    <input className="input" type="number" value={form.valor} style={inp}
                      onChange={e => { precioAutoRef.current = null; setForm(f => ({ ...f, valor: e.target.value })); }} required min="0" /></div>
                  <div><label className="label">Domicilio</label>
                    <input className="input" type="number" value={form.domicilio} style={inp} placeholder="0"
                      onChange={e => setForm(f => ({ ...f, domicilio: e.target.value }))} min="0" />
                    {domicilioCalc > 0 && (
                      <div style={{ fontSize: 10, color: '#0284c7', marginTop: 3 }}>
                        Total: {fmt(valorTotalCalc)}
                      </div>
                    )}</div>
                  <div><label className="label">Costo</label>
                    <input className="input" type="number" value={form.costo} style={inp}
                      onChange={e => setForm(f => ({ ...f, costo: e.target.value }))} min="0" /></div>
                  <div><label className="label" title="% comisión sobre el valor (ej: 3%)">Comisión %</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input className="input" type="number" value={form.comision_pct} style={inp} placeholder="0"
                        onChange={e => setForm(f => ({ ...f, comision_pct: e.target.value }))} min="0" max="100" step="0.1" />
                    </div></div>
                  <div><label className="label">Utilidad</label>
                    <input className="input" value={fmt(utilCalc)} readOnly
                      style={{ background: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.2)', color: '#059669', fontWeight: 700 }} />
                    {comisionCalc > 0 && (
                      <div style={{ fontSize: 10, color: '#b45309', marginTop: 3 }}>
                        − {fmt(comisionCalc)} comisión ({form.comision_pct}%)
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-grid3" style={{ marginBottom: 12 }}>
                  <div><label className="label">Factura</label>
                    <select className="input" value={form.factura} style={inp} onChange={e => setForm(f => ({ ...f, factura: e.target.value }))}>
                      <option value="NO">NO</option>
                      <option value="SI">SÍ — 19% IVA</option>
                    </select></div>
                  <div><label className="label">N° Factura <span style={{ color: '#b3b3b3', fontWeight: 400 }}>(opcional)</span></label>
                    <input className="input" value={form.numero_factura} style={inp} placeholder="Ej: FV-001"
                      onChange={e => setForm(f => ({ ...f, numero_factura: e.target.value }))} /></div>
                  <div><label className="label">IVA 19%</label>
                    {form.factura === 'SI' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#b45309', marginBottom: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!form.con_iva} onChange={e => setForm(f => ({ ...f, con_iva: e.target.checked ? 1 : 0 }))} style={{ accentColor: '#b45309' }} />
                        Aplicar IVA
                      </label>
                    )}
                    <input className="input" value={fmt(ivaCalc)} readOnly
                      style={{ background: 'rgba(180,83,9,0.06)', border: '1px solid rgba(180,83,9,0.2)', color: form.con_iva ? '#b45309' : '#b3b3b3', fontWeight: 700 }} /></div>
                </div>

                <div style={{ marginBottom: 12, padding: '12px 14px', border: '1px solid #e8eef2', background: pagoDivididoActivo ? 'rgba(99,102,241,0.04)' : '#fff', borderRadius: 2 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#08293f', fontSize: 12, fontWeight: 800 }}>
                    <input type="checkbox" checked={pagoDivididoActivo} onChange={e => setForm(f => ({ ...f, pago_dividido_aplica: e.target.checked ? 1 : 0 }))} style={{ accentColor: '#6366f1' }} />
                    Habilitar pago dividido
                  </label>
                  {pagoDivididoActivo && (
                    <>
                      <div className="form-grid3" style={{ marginTop: 10 }}>
                        <div>
                          <label className="label">Cobros / meses</label>
                          <input className="input" type="number" min="2" max="24" value={form.pago_cuotas_total} style={inp}
                            onChange={e => setForm(f => ({ ...f, pago_cuotas_total: e.target.value }))} />
                        </div>
                        <div>
                          <label className="label">Facturación</label>
                          <select className="input" value={form.pago_factura_modo} style={inp} onChange={e => setForm(f => ({ ...f, pago_factura_modo: e.target.value, factura: 'SI' }))}>
                            <option value="UNICA">1 factura total</option>
                            <option value="MULTIPLE">Varias facturas mensuales</option>
                          </select>
                        </div>
                        <div>
                          <label className="label">Cobro aproximado</label>
                          <input className="input" value={fmt(pagoCuotaBaseCalc)} readOnly
                            style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.22)', color: '#6366f1', fontWeight: 800 }} />
                        </div>
                      </div>
                      <div style={{ color: '#5a7a8a', fontSize: 11, lineHeight: 1.45, marginTop: 8 }}>
                        {form.pago_factura_modo === 'UNICA'
                          ? 'La primera cuota guarda la factura total para IVA y seguridad social; las siguientes quedan como cobros ya facturados.'
                          : 'Cada mes queda con su cobro y su factura mensual para seguridad social e IVA.'}
                      </div>
                    </>
                  )}
                </div>

                <div className="form-grid3" style={{ marginBottom: 12 }}>
                  <div>
                    <label className="label">¿Tiene ReteIVA?</label>
                    <select className="input" value={reteivaModo} style={inp} onChange={e => setForm(f => ({ ...f, reteiva_modo: e.target.value, reteiva_aplica: e.target.value === 'NO' ? 0 : 1, reteiva_pago_empresa: e.target.value === 'PAGO' ? f.reteiva_pago_empresa : '' }))}>
                      <option value="NO">NO</option>
                      <option value="PAGO">SÍ — por valor pagado</option>
                      <option value="PCT">SÍ — automático 15% del IVA</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">{reteivaModo === 'PCT' ? 'Porcentaje sobre IVA' : 'Valor pagado por empresa'}</label>
                    {reteivaModo === 'PCT' ? (
                      <input className="input" type="number" value={form.reteiva_pct ?? RETEIVA_PCT_DEFAULT} style={inp}
                        onChange={e => setForm(f => ({ ...f, reteiva_pct: e.target.value }))} min="0" max="100" step="0.1" />
                    ) : (
                      <input className="input" type="number" value={form.reteiva_pago_empresa} disabled={reteivaModo !== 'PAGO'} style={{ ...inp, opacity: reteivaModo === 'PAGO' ? 1 : 0.55 }} placeholder={reteivaModo === 'PAGO' ? 'Valor recibido' : 'No aplica'}
                      onChange={e => setForm(f => ({ ...f, reteiva_pago_empresa: e.target.value }))} min="0" />
                    )}
                  </div>
                  <div>
                    <label className="label">ReteIVA calculado</label>
                    <input className="input" value={fmt(reteivaCalc)} readOnly
                      style={{ background: 'rgba(14,165,233,0.07)', border: '1px solid rgba(14,165,233,0.22)', color: reteivaModo !== 'NO' ? '#0284c7' : '#b3b3b3', fontWeight: 700 }} />
                    {reteivaModo !== 'NO' && (
                      <div style={{ fontSize: 10, color: '#0284c7', marginTop: 3 }}>
                        {reteivaModo === 'PCT' ? `IVA ${fmt(ivaCalc)} × ${reteivaPct}%` : `Total con IVA: ${fmt(totalConIvaCalc)}`}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-grid2" style={{ marginBottom: 12 }}>
                  <div><label className="label">Estado</label>
                    <select className="input" value={form.estado} style={inp} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                      {ESTADOS_REGISTRO.map(e => <option key={e} value={e}>{e}</option>)}
                    </select></div>
                </div>

                <div style={{ marginBottom: 20 }}><label className="label">Notas</label>
                  <textarea className="input" value={form.notas} rows={3} style={{ ...inp, resize: 'vertical', fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1.5 }}
                    onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} /></div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="submit" disabled={loading} className="btn-primary" style={{ flex: 1, padding: '10px', letterSpacing: '0.05em' }}>
                    {loading ? 'Guardando...' : duplicando ? 'Crear duplicado' : editId ? 'Actualizar' : 'Guardar'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setEditId(null); setDuplicando(false); }}>Cancelar</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showReteivaLote && clienteResumenSeleccionado && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 620 }}>
            <div style={{ padding: '24px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                <div>
                  <h2 style={{ fontSize: 13, fontWeight: 700, color: '#08293f', margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Aplicar ReteIVA
                  </h2>
                  <div style={{ width: 28, height: 2, background: '#00b8e3', marginTop: 5 }} />
                </div>
                <button onClick={() => setShowReteivaLote(false)}
                  style={{ fontSize: 22, fontWeight: 300, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
              </div>

              <div style={{ background: '#08293f', color: '#fff', padding: '14px 16px', borderRadius: 2, marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(0,184,227,0.8)', letterSpacing: '0.09em', textTransform: 'uppercase' }}>{clienteResumenSeleccionado.cliente}</div>
                <div style={{ marginTop: 5, fontSize: 13, color: 'rgba(255,255,255,0.76)' }}>{serviciosReteivaSeleccionados.length} servicios seleccionados</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
                <div style={{ border: '1px solid #e8eef2', padding: '10px 12px' }}>
                  <div className="label">Total + IVA</div>
                  <b style={{ color: '#6366f1', fontSize: 15 }}>{fmt(totalReteivaGrupo)}</b>
                </div>
                <div style={{ border: '1px solid #e8eef2', padding: '10px 12px' }}>
                  <div className="label">Pagado por empresa</div>
                  <input className="input" type="number" value={reteivaPagoGrupo} style={{ ...inp, marginTop: 4 }} placeholder="Valor recibido"
                    min="0" onChange={e => setReteivaPagoGrupo(e.target.value)} autoFocus />
                </div>
                <div style={{ border: '1px solid #d8e8f1', padding: '10px 12px', background: 'rgba(14,165,233,0.06)' }}>
                  <div className="label">ReteIVA a repartir</div>
                  <b style={{ color: '#0284c7', fontSize: 15 }}>{fmt(reteivaGrupoCalc)}</b>
                </div>
              </div>

              <div style={{ border: '1px solid #e8eef2', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 8, padding: '9px 11px', background: '#f9fafb', color: '#5a7a8a', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  <span>Servicio</span><span style={{ textAlign: 'right' }}>Total</span><span style={{ textAlign: 'right' }}>ReteIVA</span>
                </div>
                {serviciosReteivaSeleccionados.map((r, index) => {
                  const totalServicio = (parseFloat(r.valor) || 0) + (parseFloat(r.valor_iva) || 0);
                  const previo = serviciosReteivaSeleccionados.slice(0, index).reduce((s, item) => {
                    const itemTotal = (parseFloat(item.valor) || 0) + (parseFloat(item.valor_iva) || 0);
                    return s + Math.round(reteivaGrupoCalc * (itemTotal / (totalReteivaGrupo || 1)));
                  }, 0);
                  const reteivaServicio = index === serviciosReteivaSeleccionados.length - 1
                    ? Math.max(0, reteivaGrupoCalc - previo)
                    : Math.round(reteivaGrupoCalc * (totalServicio / (totalReteivaGrupo || 1)));
                  return (
                    <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 8, padding: '10px 11px', borderTop: '1px solid #edf3f7', alignItems: 'center' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#08293f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombreServicio(r) || '—'}</div>
                        <div style={{ fontSize: 10, color: '#8aa0ad', marginTop: 2 }}>{r.fecha?.slice(0,10)}</div>
                      </div>
                      <b style={{ textAlign: 'right', fontSize: 12, color: '#6366f1' }}>{fmt(totalServicio)}</b>
                      <b style={{ textAlign: 'right', fontSize: 12, color: '#0284c7' }}>{fmt(reteivaServicio)}</b>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" disabled={loading || !serviciosReteivaSeleccionados.length} className="btn-primary" onClick={aplicarReteivaGrupo} style={{ flex: 1, padding: '10px', letterSpacing: '0.05em' }}>
                  {loading ? 'Aplicando...' : 'Aplicar reparto'}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setShowReteivaLote(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabla desktop / Cards móvil */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Registros del periodo</div>
        <div style={{ display: 'flex', gap: 6, padding: 4, border: '1px solid #e8eef2', background: '#edf4fa' }}>
          <select value={`${ordenCol}:${ordenDir}`} onChange={e => {
            const [col, dir] = e.target.value.split(':');
            setOrdenCol(col);
            setOrdenDir(dir);
          }} className="input" style={{ width: 'auto', minWidth: 180, padding: '5px 8px', fontSize: 11, background: 'var(--app-input)', border: '1px solid var(--app-border)' }}>
            <option value="cliente:asc">Nombre A → Z</option>
            <option value="cliente:desc">Nombre Z → A</option>
            <option value="fecha:desc">Fecha descendente</option>
            <option value="fecha:asc">Fecha ascendente</option>
          </select>
          <button onClick={() => setVistaRegistros('tabla')}
            style={{ fontSize: 11, fontWeight: 800, padding: '6px 12px', borderRadius: 2, cursor: 'pointer', fontFamily: 'inherit',
              background: vistaRegistros === 'tabla' ? '#08293f' : 'transparent', color: vistaRegistros === 'tabla' ? '#00b8e3' : '#5a7a8a', border: 'none' }}>Tabla</button>
          <button onClick={() => setVistaRegistros('estados')}
            style={{ fontSize: 11, fontWeight: 800, padding: '6px 12px', borderRadius: 2, cursor: 'pointer', fontFamily: 'inherit',
              background: vistaRegistros === 'estados' ? '#08293f' : 'transparent', color: vistaRegistros === 'estados' ? '#00b8e3' : '#5a7a8a', border: 'none' }}>Estados</button>
        </div>
      </div>

      {vistaRegistros === 'tabla' ? (
        <div className="tabla-desktop">
          <div style={{ display: 'grid', gap: 12 }} className="registros-integrada-layout">
            <div className="registros-clientes-panel" style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, overflow: 'hidden' }}>
              <div className="registros-tabla-cabecera" style={{ padding: '10px 13px', borderBottom: '1px solid #e8eef2', background: '#f9fafb' }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>— Registros</div>
                  <div style={{ fontSize: 12, color: '#b3b3b3', marginTop: 2 }}>Vista principal para revisar, buscar y abrir detalle</div>
                </div>
              </div>
              <div className="registros-clientes-scroll" style={{ overflowX: 'hidden', maxHeight: 660 }}>
                <table className="tbl registros-clientes-tabla" style={{ margin: 0, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '24%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <SortTh col="cliente" label="Cliente" />
                      <th style={{ textAlign: 'center' }}>Serv.</th>
                      <th>Último</th>
                      <SortTh col="valor" label="Valor" style={{ textAlign: 'right' }} />
                      <th style={{ textAlign: 'right' }}>Utilidad</th>
                      <th style={{ textAlign: 'right' }}>IVA</th>
                      <th style={{ textAlign: 'right' }}>Total + IVA</th>
                      <th style={{ textAlign: 'right' }}>ReteIVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesIntegrados.map(c => {
                      const activo = clienteResumenSeleccionado?.key === c.key;
                      const abierto = !!expandidos[`integrada:${c.key}`];
                      return (
                        <Fragment key={c.key}>
                          <tr onClick={() => setClienteSeleccionadoKey(c.key)}
                            style={{ cursor: 'pointer', background: activo ? '#f0fbff' : '#fff' }}>
                            <td className="registros-cliente-celda">
                              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <button onClick={e => { e.stopPropagation(); toggleExpandido(`integrada:${c.key}`); }}
                                  title={abierto ? 'Contraer servicios' : 'Expandir servicios'}
                                  style={{ width: 22, height: 22, border: '1px solid #e8eef2', background: abierto ? '#08293f' : '#fff', color: abierto ? '#00b8e3' : '#5a7a8a', cursor: 'pointer', borderRadius: 2, fontWeight: 800 }}>
                                  {abierto ? '−' : '+'}
                                </button>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 900, color: '#08293f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.cliente}</div>
                                  <div style={{ fontSize: 10, color: '#b3b3b3', marginTop: 2 }}>{c.esGrupoEmpresa ? `${c._nombres.size} contactos agrupados` : 'Cliente'}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ color: '#5a7a8a', fontSize: 12, textAlign: 'center' }}>{c.items.length}</td>
                            <td style={{ color: '#5a7a8a', whiteSpace: 'nowrap', fontSize: 12 }}>{c.ultimaFecha?.slice(0,10) || '—'}</td>
                            <td style={{ textAlign: 'right', fontWeight: 900, color: '#08293f', whiteSpace: 'nowrap' }}>{fmt(c.totalValor)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 900, color: '#059669', whiteSpace: 'nowrap' }}>{fmt(c.totalUtilidad)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 800, color: '#b45309', whiteSpace: 'nowrap' }}>{fmt(c.totalIva)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 900, color: '#6366f1', whiteSpace: 'nowrap' }}>{fmt(c.totalValor + c.totalIva)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 900, color: '#0284c7', whiteSpace: 'nowrap' }}>{fmt(c.totalReteiva)}</td>
                          </tr>
                          {abierto && c.items.map(r => {
                            const itemIva = parseFloat(r.valor_iva) || 0;
                            const itemTotalIva = (parseFloat(r.valor) || 0) + itemIva;
                            return (
                            <tr key={r.id} style={{ background: '#f9fbfd', borderLeft: r.no_facturo ? '3px solid #f59e0b' : '3px solid transparent' }}>
                              <td style={{ paddingLeft: 52 }}>
                                <div style={{ fontWeight: 700, color: '#08293f', fontSize: 12 }}>{nombreServicio(r) || '—'}</div>
                                <div style={{ fontSize: 10, color: '#b3b3b3', marginTop: 2 }}>{r.fecha?.slice(0,10)}{r.solicitante ? ` · ${r.solicitante}` : ''}</div>
                                {r.pago_dividido_aplica ? (
                                  <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 800, marginTop: 3 }}>
                                    Cobro {r.pago_cuota_num}/{r.pago_cuotas_total} · {r.pago_factura_estado === 'YA_FACTURADO' ? 'ya facturado' : r.pago_factura_modo === 'UNICA' ? 'factura total' : 'factura mensual'}
                                    <PlanResumen r={r} onVer={verPlan} />
                                  </div>
                                ) : null}
                                <RegistroAcciones
                                  registro={r}
                                  onMarcarPagado={marcarPagado}
                                  onEditar={editar}
                                  onDuplicar={duplicar}
                                  onToggleNoFacturo={toggleNoFacturo}
                                  onEliminar={eliminar}
                                />
                              </td>
                              <td><Tag text={r.estado} /></td>
                              <td style={{ color: '#5a7a8a', fontSize: 11 }}>{r.tipo || '—'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#08293f' }}>{fmt(r.valor)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmt(r.utilidad)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#b45309' }}>{fmt(itemIva)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 800, color: '#6366f1' }}>{fmt(itemTotalIva)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 800, color: '#0284c7' }}>{fmt(r.reteiva_valor)}</td>
                            </tr>
                          );})}
                        </Fragment>
                      );
                    })}
                    {clientesIntegrados.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: '#b3b3b3', fontSize: 13 }}>Sin registros</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="registros-empresa-panel" style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
              <div className="registros-empresa-hero" style={{ background: '#08293f', border: '1px solid rgba(0,184,227,0.22)', borderRadius: 2, padding: '13px 15px' }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(0,184,227,0.75)', marginBottom: 8 }}>— Cliente seleccionado</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 22, lineHeight: 1.05, fontWeight: 900, color: '#fff' }}>{clienteResumenSeleccionado?.cliente || 'Sin cliente'}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.68)', marginTop: 7 }}>
                      {clienteResumenSeleccionado ? `${clienteResumenSeleccionado.items.length} servicios · Último ${clienteResumenSeleccionado.ultimaFecha?.slice(0,10) || '—'}` : 'Selecciona un cliente de la tabla'}
                    </div>
                  </div>
                  {clienteResumenSeleccionado?.esGrupoEmpresa && <span style={{ fontSize: 9, fontWeight: 800, background: 'rgba(99,102,241,0.14)', color: '#c7d2fe', padding: '3px 8px', borderRadius: 2 }}>GRUPO</span>}
                </div>
              </div>

              <div className="registros-empresa-body" style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '10px' }}>
                {clienteResumenSeleccionado ? (
                  <>
                    <div className="registros-empresa-resumen">
                      {[
                        ['Servicios', clienteResumenSeleccionado.items.length],
                        ['Valor vendido', fmt(clienteResumenSeleccionado.totalValor)],
                        ['Costo', fmt(clienteResumenSeleccionado.totalCosto)],
                        ['Utilidad', fmt(clienteResumenSeleccionado.totalUtilidad), '#059669'],
                        ['IVA', fmt(clienteResumenSeleccionado.totalIva), '#b45309'],
                        ['Total + IVA', fmt(clienteResumenSeleccionado.totalValor + clienteResumenSeleccionado.totalIva), '#6366f1'],
                        ['Base facturada SS', fmt(clienteResumenSeleccionado.totalFacturadoSS), '#0284c7'],
                        ['Por cobrar', fmt(clienteResumenSeleccionado.totalPendiente), '#dc2626'],
                        ['ReteIVA', fmt(clienteResumenSeleccionado.totalReteiva), '#0284c7'],
                      ].filter(([label]) => label !== 'ReteIVA' || clienteResumenSeleccionado.totalIva > 0 || clienteResumenSeleccionado.totalReteiva > 0).map(([label, value, color]) => (
                        <div className="registros-empresa-metrica" key={label}>
                          <span>{label}</span>
                          <b style={{ color: color || '#08293f' }}>{value}</b>
                        </div>
                      ))}
                    </div>
                    <div className="registros-servicios-box" style={{ marginTop: 8, border: '1px solid #e8eef2', borderRadius: 2, overflow: 'visible' }}>
                      <div style={{ padding: '8px 12px', background: '#f9fafb', borderBottom: '1px solid #e8eef2', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5a7a8a' }}>Servicios del cliente</span>
                        <button
                          type="button"
                          onClick={() => setReteivaSeleccionados(new Set(clienteResumenSeleccionado.items.map(r => r.id)))}
                          style={{ fontSize: 10, fontWeight: 800, color: '#0284c7', background: '#fff', border: '1px solid #d8e8f1', padding: '4px 8px', cursor: 'pointer', borderRadius: 2 }}
                        >
                          Seleccionar todos
                        </button>
                      </div>
                      {clienteResumenSeleccionado.items.map(r => (
                        <div className="registros-servicio-fila" key={r.id} style={{ padding: '7px 9px', borderBottom: '1px solid #edf3f7', borderLeft: r.no_facturo ? '3px solid #f59e0b' : '3px solid transparent' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                            <div style={{ minWidth: 0, display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                              <input
                                type="checkbox"
                                checked={reteivaSeleccionados.has(r.id)}
                                onChange={() => toggleReteivaSeleccion(r.id)}
                                title="Seleccionar servicio (ReteIVA o recibo)"
                                style={{ marginTop: 2, accentColor: '#0284c7', cursor: 'pointer' }}
                              />
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 12, fontWeight: 800, color: '#08293f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombreServicio(r) || '—'}</div>
                                <div style={{ fontSize: 10, color: '#b3b3b3', marginTop: 2 }}>
                                  {r.fecha?.slice(0,10)}{r.solicitante ? ` · ${r.solicitante}` : ''}{r.no_facturo ? ' · no facturado por mí' : ''}{esFacturado(r) ? ' · con factura' : ''}
                                  {(parseFloat(r.reteiva_valor) || 0) > 0 && <span style={{ color: '#0284c7' }}> · ReteIVA {fmt(r.reteiva_valor)}</span>}
                                </div>
                                {r.pago_dividido_aplica ? (
                                  <Fragment>
                                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 5 }}>
                                      <span style={{ fontSize: 9, fontWeight: 800, color: '#6366f1', background: 'rgba(99,102,241,0.1)', padding: '2px 6px', borderRadius: 2 }}>Cobro {r.pago_cuota_num}/{r.pago_cuotas_total}</span>
                                      <span style={{ fontSize: 9, fontWeight: 800, color: '#0284c7', background: 'rgba(14,165,233,0.1)', padding: '2px 6px', borderRadius: 2 }}>
                                        {r.pago_factura_estado === 'YA_FACTURADO' ? 'Ya facturado' : r.pago_factura_modo === 'UNICA' ? 'Factura total' : 'Factura mensual'}
                                      </span>
                                    </div>
                                    <PlanResumen r={r} onVer={verPlan} />
                                  </Fragment>
                                ) : null}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 900, color: '#08293f' }}>{fmt(r.valor)}</div>
                              <div style={{ fontSize: 10, fontWeight: 800, color: '#059669', marginTop: 2 }}>{fmt(r.utilidad)}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
                            <Tag text={r.estado} />
                            <RegistroAcciones
                              registro={r}
                              onMarcarPagado={marcarPagado}
                              onEditar={editar}
                              onDuplicar={duplicar}
                              onToggleNoFacturo={toggleNoFacturo}
                              onEliminar={eliminar}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      <button className="btn-primary" onClick={() => toggleExpandido(`integrada:${clienteResumenSeleccionado.key}`)} style={{ padding: '7px 14px', fontSize: 12 }}>
                        {expandidos[`integrada:${clienteResumenSeleccionado.key}`] ? 'Contraer servicios' : 'Ver servicios'}
                      </button>
                      <button type="button" className="btn-ui btn-ui--md btn-ui--outline"
                        disabled={!clienteResumenSeleccionado.items.some(r => reteivaSeleccionados.has(r.id) && !esFacturado(r))}
                        title="Recibo: solo para servicios que NO se facturan (sin IVA). Marca los servicios y genera el recibo"
                        onClick={() => setReciboAbierto(true)}>
                        Generar recibo ({clienteResumenSeleccionado.items.filter(r => reteivaSeleccionados.has(r.id) && !esFacturado(r)).length})
                      </button>
                      {clienteResumenSeleccionado.items.some(r => reteivaSeleccionados.has(r.id) && (parseFloat(r.valor_iva) || 0) > 0) && (
                        <button onClick={() => setShowReteivaLote(true)} className="btn-secondary" style={{ padding: '7px 14px', fontSize: 12, color: '#0284c7', borderColor: 'rgba(14,165,233,0.28)' }}>
                          Aplicar ReteIVA ({reteivaSeleccionados.size})
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '28px 0', textAlign: 'center', color: '#b3b3b3', fontSize: 12 }}>Sin cliente seleccionado</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : vistaRegistros === 'legacy' ? (
      <div className="tabla-desktop">
        {/* Toggle agrupado / lista */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 6 }}>
          <button onClick={() => setAgrupado(false)}
            style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 2, cursor: 'pointer', fontFamily: 'inherit',
              background: !agrupado ? '#08293f' : '#fff', color: !agrupado ? '#00b8e3' : '#5a7a8a',
              border: `1px solid ${!agrupado ? '#08293f' : '#e8eef2'}` }}>≡ Lista</button>
          <button onClick={() => setAgrupado(true)}
            style={{ fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 2, cursor: 'pointer', fontFamily: 'inherit',
              background: agrupado ? '#08293f' : '#fff', color: agrupado ? '#00b8e3' : '#5a7a8a',
              border: `1px solid ${agrupado ? '#08293f' : '#e8eef2'}` }}>⊞ Por cliente</button>
        </div>

        {agrupado ? (
          /* ── Vista agrupada por cliente ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* ── Panel agrupación (modo validación) ── */}
            {modoVal && msgAgrup && (
              <div style={{ background: msgAgrup.startsWith('Error') ? 'rgba(220,38,38,0.07)' : 'rgba(5,150,105,0.07)', border: `1px solid ${msgAgrup.startsWith('Error') ? 'rgba(220,38,38,0.25)' : 'rgba(5,150,105,0.25)'}`, borderRadius: 2, padding: '8px 14px', fontSize: 12, fontWeight: 600, color: msgAgrup.startsWith('Error') ? '#dc2626' : '#059669' }}>
                {msgAgrup}
              </div>
            )}
            {modoVal && selAgrup.size > 0 && (
              <div style={{ background: '#f9fafb', border: '1.5px solid rgba(99,102,241,0.35)', borderRadius: 2, padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', whiteSpace: 'nowrap' }}>
                  {selAgrup.size} cliente{selAgrup.size > 1 ? 's' : ''} seleccionado{selAgrup.size > 1 ? 's' : ''}:
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
                  {[...selAgrup].map(n => (
                    <span key={n} style={{ fontSize: 10, background: 'rgba(99,102,241,0.1)', color: '#6366f1', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{n}</span>
                  ))}
                </div>
                <input list="grupos-agrup-list" value={destGrupo} onChange={e => setDestGrupo(e.target.value)}
                  className="input" style={{ minWidth: 180, fontSize: 12 }}
                  placeholder="Empresa / grupo destino..." />
                <datalist id="grupos-agrup-list">
                  {gruposDisponibles.map(g => <option key={g.id} value={g.nombre} />)}
                </datalist>
                <button onClick={guardarAgrupacion} disabled={!destGrupo.trim() || savingAgrup}
                  className="btn-primary" style={{ fontSize: 12, padding: '6px 14px', opacity: !destGrupo.trim() ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                  {savingAgrup ? 'Guardando...' : '⇒ Guardar en grupo'}
                </button>
                <button onClick={() => { setSelAgrup(new Set()); setDestGrupo(''); }}
                  style={{ fontSize: 11, color: '#b3b3b3', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Limpiar</button>
              </div>
            )}

            {/* ── Encabezado columnas grupo ── */}
            <div style={{ display: 'flex', gap: 10, padding: '4px 16px', alignItems: 'center' }}>
              <span style={{ minWidth: 16 }}></span>
              <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: '#b3b3b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cliente</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#b3b3b3', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Items</span>
              <span style={{ minWidth: 110, textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#b3b3b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Valor</span>
              <span style={{ minWidth: 90, textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#b3b3b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Utilidad</span>
              <span style={{ minWidth: 80, textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#b3b3b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>IVA</span>
              <span style={{ minWidth: 110, textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#b3b3b3', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total</span>
            </div>
            {grupos.map(([cliente, g]) => {
              // ── Meta-grupo "Servicios individuales" ──────────────
              if (g.isSingles) {
                const abierto = !!expandidos['__singles__'];
                const visiblesCount = modoVal ? g.items.filter(r => !validados.has(r.id)).length : g.items.length;
                if (modoVal && visiblesCount === 0) return null;
                return (
                  <div key="__singles__" className="card" style={{ padding: 0, overflow: 'hidden', border: '1px solid #e8eef2' }}>
                    <button onClick={() => toggleExpandido('__singles__')}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px',
                        background: '#fafbfc', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                      <span style={{ fontSize: 13, color: abierto ? '#00b8e3' : '#5a7a8a', fontWeight: 700, minWidth: 16 }}>{abierto ? '▾' : '▸'}</span>
                      <span style={{ fontWeight: 600, color: '#5a7a8a', fontSize: 12, fontStyle: 'italic', flex: 1 }}>
                        Servicios individuales <span style={{ fontSize: 10, color: '#b3b3b3', fontStyle: 'normal' }}>({g.clients.length} clientes)</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#08293f', minWidth: 110, textAlign: 'right' }}>{fmt(g.totalValor)}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#059669', minWidth: 90, textAlign: 'right' }}>{fmt(g.totalUtilidad)}</span>
                      <span style={{ fontSize: 11, color: '#b45309', minWidth: 80, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(g.totalIva)}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#08293f', minWidth: 110, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(g.totalValor + g.totalIva)}</span>
                    </button>
                    {abierto && (
                      <div style={{ borderTop: '1px solid #f0f4f8' }}>
                        <table className="tbl" style={{ margin: 0 }}>
                          <thead>
                            <tr style={{ background: '#f9fafb' }}>
                              {modoVal && <th style={{ width: 28 }}></th>}
                              {modoVal && <th style={{ width: 28 }}></th>}
                              <th>Cliente</th><th>Servicio</th><th>Tipo</th>
                              <th style={{ textAlign: 'right' }}>Valor</th>
                              <th style={{ textAlign: 'right' }}>Utilidad</th>
                              <th style={{ textAlign: 'right' }}>IVA</th>
                              <th style={{ textAlign: 'right' }}>Total</th>
                              <th>Estado</th><th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.clients.map(([cli, cg]) => {
                              const r = cg.items[0];
                              if (modoVal && validados.has(r.id)) return null;
                              return (
                                <tr key={cli} style={{ background: selAgrup.has(cli) ? 'rgba(99,102,241,0.04)' : 'transparent', borderLeft: r.no_facturo ? '3px solid #f59e0b' : '3px solid transparent' }}>
                                  {modoVal && (
                                    <td>
                                      <button onClick={() => toggleSelAgrup(cli)} title="Seleccionar para agrupar"
                                        style={{ fontSize: 14, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'none', border: 'none', margin: 'auto',
                                          color: selAgrup.has(cli) ? '#6366f1' : '#b3b3b3' }}>
                                        {selAgrup.has(cli) ? '☑' : '☐'}
                                      </button>
                                    </td>
                                  )}
                                  {modoVal && (
                                    <td>
                                      <button onClick={() => toggleValidar(r.id)} title="Marcar como validado"
                                        style={{ fontSize: 12, width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                          background: 'rgba(99,102,241,0.08)', border: '1.5px solid #6366f1', color: '#6366f1', margin: 'auto' }}>○</button>
                                    </td>
                                  )}
                                  <td style={{ fontWeight: 700, color: '#08293f', fontSize: 12, whiteSpace: 'nowrap' }}>{cli}</td>
                                  <td style={{ color: '#5a7a8a', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                                    {nombreServicio(r)}
                                    {r.codigo_dian && <span style={{ marginLeft: 4, fontSize: 9, color: '#b3b3b3', fontWeight: 600 }}>[{r.codigo_dian}]</span>}
                                    {r.solicitante && <div style={{ fontSize: 10, color: '#b3b3b3' }}>{r.solicitante}</div>}
                                  </td>
                                  <td><span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 6px',
                                    background: r.tipo === 'FIJO' ? 'rgba(8,41,63,0.08)' : 'rgba(0,184,227,0.08)',
                                    color: r.tipo === 'FIJO' ? '#08293f' : '#0284c7' }}>{r.tipo}</span></td>
                                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#08293f' }}>{fmt(r.valor)}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 600, color: '#059669' }}>{fmt(r.utilidad)}</td>
                                  <td style={{ textAlign: 'right', color: '#b45309', fontSize: 12 }}>{fmt(r.valor_iva)}</td>
                                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#6366f1', fontSize: 12 }}>{fmt((parseFloat(r.valor)||0) + (parseFloat(r.valor_iva)||0))}</td>
                                  <td><Tag text={r.estado} /></td>
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    <RegistroAcciones
                                      registro={r}
                                      onMarcarPagado={marcarPagado}
                                      onEditar={editar}
                                      onDuplicar={duplicar}
                                      onToggleNoFacturo={toggleNoFacturo}
                                      onEliminar={eliminar}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              }

              // ── Grupo normal (multi-servicio) ─────────────────────
              const abierto = !!expandidos[cliente];
              const itemsVisibles = modoVal ? g.items.filter(r => !validados.has(r.id)) : g.items;
              if (modoVal && itemsVisibles.length === 0) return null;
              return (
                <div key={cliente} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  {modoVal && (
                    <button onClick={e => { e.stopPropagation(); toggleSelAgrup(cliente); }}
                      title="Seleccionar para agrupar"
                      style={{ padding: '0 12px', background: selAgrup.has(cliente) ? 'rgba(99,102,241,0.1)' : 'transparent',
                        border: 'none', borderRight: '1px solid #e8eef2', cursor: 'pointer',
                        color: selAgrup.has(cliente) ? '#6366f1' : '#b3b3b3', fontSize: 16, flexShrink: 0 }}>
                      {selAgrup.has(cliente) ? '☑' : '☐'}
                    </button>
                  )}
                  <button onClick={() => toggleExpandido(cliente)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px',
                      background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                    <span style={{ fontSize: 13, color: abierto ? '#00b8e3' : '#5a7a8a', fontWeight: 700, minWidth: 16 }}>{abierto ? '▾' : '▸'}</span>
                    <span style={{ fontWeight: 700, color: '#08293f', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cliente}</span>
                    {g.esGrupoEmpresa && <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(99,102,241,0.1)', color: '#6366f1', padding: '2px 6px', borderRadius: 10, whiteSpace: 'nowrap' }}>EMPRESA</span>}
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 11, color: '#5a7a8a', whiteSpace: 'nowrap' }}>
                      {modoVal ? `${itemsVisibles.length}/${g.items.length}` : `${g.items.length}`} {g.items.length === 1 ? 'item' : 'items'}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#08293f', minWidth: 110, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(g.totalValor)}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#059669', minWidth: 90, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(g.totalUtilidad)}</span>
                    <span style={{ fontSize: 11, color: '#b45309', minWidth: 80, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(g.totalIva)}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', minWidth: 110, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(g.totalValor + g.totalIva)}</span>
                  </button>
                  </div>
                  {abierto && (
                    <div style={{ borderTop: '1px solid #f0f4f8' }}>
                      <table className="tbl" style={{ margin: 0 }}>
                        <thead>
                          <tr style={{ background: '#f9fafb' }}>
                            {modoVal && <th style={{ width: 28 }}></th>}
                            {g.esGrupoEmpresa && <th style={{ color: '#6366f1' }}>Cliente</th>}
                            <th>Fecha</th><th>Servicio</th><th>Tipo</th>
                            <th style={{ textAlign: 'right' }}>Valor</th>
                            <th style={{ textAlign: 'right' }}>Utilidad</th>
                            <th style={{ textAlign: 'right' }}>IVA</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th>Estado</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map(r => {
                            if (modoVal && validados.has(r.id)) return null;
                            return (
                              <tr key={r.id} style={{ background: 'transparent', borderLeft: r.no_facturo ? '3px solid #f59e0b' : '3px solid transparent' }}>
                                {modoVal && (
                                  <td>
                                    <button onClick={() => toggleValidar(r.id)} title="Marcar como validado"
                                      style={{ fontSize: 12, width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                        background: 'rgba(99,102,241,0.08)', border: '1.5px solid #6366f1', color: '#6366f1', margin: 'auto' }}>○</button>
                                  </td>
                                )}
                                {g.esGrupoEmpresa && <td style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', whiteSpace: 'nowrap' }}>{nombreCliente(r)}</td>}
                                <td style={{ color: '#5a7a8a', whiteSpace: 'nowrap', fontSize: 12 }}>{r.fecha?.slice(0,10)}</td>
                                <td style={{ color: '#5a7a8a', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                                  {nombreServicio(r)}
                                  {r.codigo_dian && <span style={{ marginLeft: 4, fontSize: 9, color: '#b3b3b3', fontWeight: 600 }}>[{r.codigo_dian}]</span>}
                                  {r.solicitante && <div style={{ fontSize: 10, color: '#b3b3b3' }}>{r.solicitante}</div>}
                                </td>
                                <td><span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 6px',
                                  background: r.tipo === 'FIJO' ? 'rgba(8,41,63,0.08)' : 'rgba(0,184,227,0.08)',
                                  color: r.tipo === 'FIJO' ? '#08293f' : '#0284c7' }}>{r.tipo}</span></td>
                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#08293f' }}>{fmt(r.valor)}</td>
                                <td style={{ textAlign: 'right', fontWeight: 600, color: '#059669' }}>{fmt(r.utilidad)}</td>
                                <td style={{ textAlign: 'right', color: '#b45309', fontSize: 12 }}>{fmt(r.valor_iva)}</td>
                                <td style={{ textAlign: 'right', fontWeight: 700, color: '#6366f1', fontSize: 12 }}>{fmt((parseFloat(r.valor)||0) + (parseFloat(r.valor_iva)||0))}</td>
                                <td><Tag text={r.estado} /></td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  <RegistroAcciones
                                    registro={r}
                                    onMarcarPagado={marcarPagado}
                                    onEditar={editar}
                                    onDuplicar={duplicar}
                                    onToggleNoFacturo={toggleNoFacturo}
                                    onEliminar={eliminar}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
            {grupos.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '40px 0', color: '#b3b3b3', fontSize: 13 }}>Sin registros</div>
            )}
          </div>
        ) : (
          /* ── Vista lista flat con ordenamiento ── */
          <div className="card" style={{ overflowX: 'auto', padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <SortTh col="cliente" label="Cliente" />
                  <th>Servicio</th>
                  <th>Tipo</th>
                  <SortTh col="valor" label="Valor" style={{ textAlign: 'right' }} />
                  <th style={{ textAlign: 'right' }}>Utilidad</th>
                  <th style={{ textAlign: 'right' }}>IVA</th>
                  <SortTh col="estado" label="Estado" />
                  <th>Notas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {registrosOrdenados.map(r => (
                  <tr key={r.id} style={{ background: 'transparent', borderLeft: r.no_facturo ? '3px solid #f59e0b' : '3px solid transparent' }}>
                    <td style={{ color: '#5a7a8a', whiteSpace: 'nowrap', fontSize: 12 }}>{r.fecha?.slice(0,10)}</td>
                    <td style={{ maxWidth: 140 }}>
                      <div style={{ fontWeight: 600, color: '#08293f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombreCliente(r)}</div>
                      {r.solicitante && <div style={{ fontSize: 10, color: '#5a7a8a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.solicitante}</div>}
                    </td>
                    <td style={{ color: '#5a7a8a', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                      {nombreServicio(r)}
                      {r.codigo_dian && <span style={{ marginLeft: 4, fontSize: 9, color: '#b3b3b3', fontWeight: 600 }}>[{r.codigo_dian}]</span>}
                    </td>
                    <td><span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 6px',
                      background: r.tipo === 'FIJO' ? 'rgba(8,41,63,0.08)' : 'rgba(0,184,227,0.08)',
                      color: r.tipo === 'FIJO' ? '#08293f' : '#0284c7' }}>{r.tipo}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#08293f' }}>{fmt(r.valor)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#059669' }}>{fmt(r.utilidad)}</td>
                    <td style={{ textAlign: 'right', color: '#b45309', fontSize: 12 }}>{fmt(r.valor_iva)}</td>
                    <td><Tag text={r.estado} /></td>
                    <td style={{ color: '#b3b3b3', fontSize: 11, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notas}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <RegistroAcciones
                        registro={r}
                        onMarcarPagado={marcarPagado}
                        onEditar={editar}
                        onDuplicar={duplicar}
                        onToggleNoFacturo={toggleNoFacturo}
                        onEliminar={eliminar}
                      />
                    </td>
                  </tr>
                ))}
                {registros.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px 0', color: '#b3b3b3', fontSize: 13 }}>Sin registros</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      ) : (
        <div className="registros-estados-grid">
          {registrosPorEstado.map(col => {
            const st = ESTADO_STYLE[col.estado] || { bg: '#f4f7fa', color: '#5a7a8a' };
            return (
              <div className={`registro-estado-col${col.estado === 'RETEIVA' ? ' registro-estado-reteiva' : ''}`} key={col.estado} style={{ background: '#fff', border: '1px solid var(--app-border)', borderRadius: 2, overflow: 'visible' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderBottom: '1px solid var(--app-border)', background: '#f9fafb' }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: st.color }}>{col.estado}</div>
                    <div style={{ fontSize: 11, color: '#5a7a8a', marginTop: 2 }}>{col.items.length} registros</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 900, color: '#08293f' }}>{fmt(col.total)}</div>
                </div>
                {col.items.map(r => (
                  <div className="registro-estado-item" key={r.id} style={{ padding: '12px 14px', borderBottom: '1px solid var(--app-border)', borderLeft: r.no_facturo ? '3px solid #f59e0b' : '3px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#08293f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nombreCliente(r)}</div>
                      <div style={{ fontSize: 12, fontWeight: 900, color: col.estado === 'RETEIVA' ? '#0284c7' : '#08293f', whiteSpace: 'nowrap' }}>{fmt(col.estado === 'RETEIVA' ? r.reteiva_valor : r.valor)}</div>
                    </div>
                    <div style={{ fontSize: 11, lineHeight: 1.45, color: '#5a7a8a' }}>
                      {r.fecha?.slice(0,10)} · {nombreServicio(r) || 'Sin servicio'}<br />
                      Utilidad: <strong style={{ color: '#059669' }}>{fmt(r.utilidad)}</strong>
                      {(parseFloat(r.reteiva_valor) || 0) > 0 && <span> · ReteIVA: <strong style={{ color: '#0284c7' }}>{fmt(r.reteiva_valor)}</strong></span>}
                      {r.solicitante && <span> · {r.solicitante}</span>}
                    </div>
                    {col.estado === 'RETEIVA' ? (
                      <div className="registro-dato-derivado">Dato derivado del registro principal</div>
                    ) : (
                      <RegistroAcciones
                        registro={r}
                        onMarcarPagado={marcarPagado}
                        onEditar={editar}
                        onDuplicar={duplicar}
                        onToggleNoFacturo={toggleNoFacturo}
                        onEliminar={eliminar}
                      />
                    )}
                  </div>
                ))}
                {col.items.length === 0 && (
                  <div style={{ padding: '28px 14px', textAlign: 'center', color: '#b3b3b3', fontSize: 12 }}>Sin registros</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Cards móvil */}
      {vistaRegistros !== 'estados' && <div className="cards-mobile">
        {registros.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#b3b3b3', fontSize: 13 }}>Sin registros</div>
        )}
        {registros.map(r => (
          <div key={r.id} style={{ background: '#fff', border: '1px solid #e8eef2', padding: '14px 16px', marginBottom: 8, borderRadius: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div>
                <div style={{ fontWeight: 700, color: '#08293f', fontSize: 14 }}>{nombreCliente(r)}</div>
                {r.solicitante && <div style={{ fontSize: 11, color: '#00b8e3' }}>{r.solicitante}</div>}
                <div style={{ fontSize: 11, color: '#5a7a8a', marginTop: 2 }}>{nombreServicio(r) || '—'} · {r.fecha?.slice(0,10)}</div>
              </div>
              <Tag text={r.estado} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#08293f' }}>{fmt(r.valor)}</div>
                <div style={{ fontSize: 11, color: '#059669' }}>Utilidad: {fmt(r.utilidad)}</div>
              </div>
              <RegistroAcciones
                registro={r}
                onMarcarPagado={marcarPagado}
                onEditar={editar}
                onDuplicar={duplicar}
                onToggleNoFacturo={toggleNoFacturo}
                onEliminar={eliminar}
              />
            </div>
            {r.notas && <div style={{ fontSize: 11, color: '#b3b3b3', marginTop: 6, borderTop: '1px solid #f4f7fa', paddingTop: 6 }}>{r.notas}</div>}
          </div>
        ))}
      </div>}

      <style>{`
        .registros-page { min-height: calc(100vh - 92px); background: var(--app-bg); color: var(--app-text); }
        .registros-integrada-layout { grid-template-columns: minmax(0, 1.45fr) minmax(360px, 0.85fr); }
        .registros-clientes-tabla th, .registros-clientes-tabla td { padding: 7px 5px; }
        .registros-clientes-tabla th { font-size: 9px; letter-spacing: 0.045em; }
        .registros-clientes-tabla td { height: 48px; font-size: 11px; overflow: hidden; text-overflow: ellipsis; }
        .registros-clientes-tabla td:not(:first-child) { white-space: nowrap; }
        .registros-clientes-tabla .registros-cliente-celda { min-width: 0; }
        .registros-empresa-resumen {
          display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1px;
          border: 1px solid var(--app-border); border-radius: 3px; overflow: hidden;
          background: var(--app-border);
        }
        .registros-empresa-metrica { min-width: 0; padding: 7px 8px; background: var(--app-surface); }
        .registros-empresa-metrica span {
          display: block; color: var(--app-muted); font-size: 9px; font-weight: 700;
          letter-spacing: 0.035em; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .registros-empresa-metrica b {
          display: block; margin-top: 3px; font-size: 11px; font-weight: 900;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .registros-servicio-fila:last-child { border-bottom: 0 !important; }
        html[data-theme='dark'] .registro-kpi-card:not(.registro-kpi-card-principal),
        html[data-theme='dark'] .registro-subkpi-card,
        html[data-theme='dark'] .registros-clientes-panel,
        html[data-theme='dark'] .registros-empresa-body,
        html[data-theme='dark'] .registros-servicios-box,
        html[data-theme='dark'] .registros-tabla-cabecera {
          background: var(--app-surface) !important; border-color: var(--app-border) !important;
        }
        html[data-theme='dark'] .registros-tabla-cabecera { background: var(--app-surface-soft) !important; }
        html[data-theme='dark'] .registros-clientes-tabla tr,
        html[data-theme='dark'] .registros-clientes-tabla td { background: var(--app-surface) !important; }
        html[data-theme='dark'] .registros-page [style*='background: rgb(249, 251, 253)'],
        html[data-theme='dark'] .registros-page [style*='background: #f9fbfd'],
        html[data-theme='dark'] .registros-page [style*='background: rgb(250, 251, 252)'],
        html[data-theme='dark'] .registros-page [style*='background: #fafbfc'],
        html[data-theme='dark'] .registros-page [style*='background: rgb(237, 244, 250)'],
        html[data-theme='dark'] .registros-page [style*='background: #edf4fa'] {
          background: var(--app-surface-soft) !important;
        }
        html[data-theme='dark'] .registros-clientes-tabla tr[style*='background: rgb(240, 251, 255)'] td,
        html[data-theme='dark'] .registros-clientes-tabla tr[style*='background: #f0fbff'] td { background: var(--app-selected) !important; }
        html[data-theme='dark'] .registros-servicio-fila,
        html[data-theme='dark'] .registros-clientes-scroll { background: var(--app-surface); border-bottom-color: var(--app-border) !important; }
        html[data-theme='dark'] .registros-clientes-scroll {
          scrollbar-color: rgba(159,180,193,0.55) var(--app-surface-soft);
        }
        html[data-theme='dark'] .registros-clientes-scroll::-webkit-scrollbar-track { background: var(--app-surface-soft); }
        html[data-theme='dark'] .registros-clientes-scroll::-webkit-scrollbar-thumb { background: rgba(159,180,193,0.55); }
        .registro-acciones { display: flex; align-items: center; justify-content: flex-end; align-self: flex-end; width: 100%; gap: 5px; flex-wrap: wrap; margin-top: 9px; position: relative; }
        .registros-servicio-fila .registro-acciones { gap: 4px; margin-top: 4px; }
        .registros-servicio-fila .registro-accion { height: 28px; padding-left: 8px; padding-right: 8px; }
        .registro-accion {
          height: 30px; display: inline-flex; align-items: center; justify-content: center; gap: 5px;
          padding: 0 9px; border-radius: 6px; border: 1px solid #dce6ed; background: #fff;
          font-family: inherit; font-size: 10px; font-weight: 600; line-height: 1; cursor: pointer;
          white-space: nowrap; box-sizing: border-box;
        }
        .registro-accion:hover { filter: brightness(0.98); box-shadow: 0 1px 2px rgba(8,41,63,0.08); }
        .registro-accion:focus-visible, .registro-acciones-popover button:focus-visible { outline: 2px solid rgba(0,184,227,0.35); outline-offset: 2px; }
        .registro-accion-pagar { color: #047857; background: rgba(5,150,105,0.07); border-color: rgba(5,150,105,0.25); }
        .registro-accion-editar { color: #0284c7; background: #fff; border-color: #d8e8f1; }
        .registro-accion-mas { color: #334155; background: #f8fafc; border-color: #dce6ed; list-style: none; }
        .registro-accion-mas::-webkit-details-marker { display: none; }
        .registro-acciones-menu { position: relative; }
        .registro-acciones-menu[open] { z-index: 60; }
        .registro-acciones-popover {
          position: absolute; top: calc(100% + 5px); right: 0; width: 202px; padding: 5px;
          background: #fff; border: 1px solid #dce6ed; border-radius: 8px;
          box-shadow: 0 12px 28px rgba(8,41,63,0.17); z-index: 60;
        }
        .registro-acciones-popover button {
          width: 100%; min-height: 34px; display: flex; align-items: center; gap: 8px; padding: 7px 9px;
          border: 0; border-radius: 5px; background: transparent; color: #243b53; font-family: inherit;
          font-size: 11px; font-weight: 600; line-height: 1.25; text-align: left; cursor: pointer;
        }
        .registro-acciones-popover button:hover { background: #f4f7fa; }
        .registro-acciones-popover .registro-accion-eliminar { color: #dc2626; }
        .registro-acciones-popover .registro-accion-eliminar:hover { background: rgba(220,38,38,0.06); }
        .registro-acciones-separador { height: 1px; margin: 4px 3px; background: #e8eef2; }
        .registro-dato-derivado {
          min-height: 30px; display: flex; align-items: center; box-sizing: border-box;
          margin-top: 9px; color: #7f9aad; font-size: 10px; font-weight: 700; font-style: italic;
        }
        html[data-theme='dark'] .registro-accion,
        html[data-theme='dark'] .registro-acciones-popover { background: var(--app-surface-soft); border-color: var(--app-border); color: var(--app-text); }
        html[data-theme='dark'] .registro-acciones-popover button { color: var(--app-text); }
        html[data-theme='dark'] .registro-acciones-popover button:hover { background: var(--app-selected); }
        html[data-theme='dark'] .registro-acciones-separador { background: var(--app-border); }
        .filtros-grid { display: grid; grid-template-columns: 120px 1fr 1fr 1fr auto; gap: 12px; align-items: end; }
        .form-grid2   { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .form-grid3   { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
        .tabla-desktop { display: block; }
        .cards-mobile  { display: none; }
        .registros-estados-grid {
          display: grid; grid-template-columns: repeat(4, minmax(220px, 1fr));
          gap: 10px; align-items: start; overflow-x: auto;
        }
        .registro-estado-item:last-child { border-bottom: 0 !important; }
        @media (max-width: 768px) {
          .registros-kpi-grid { grid-template-columns: 1fr 1fr !important; }
          .registros-kpi-grid > :first-child { grid-column: 1 / -1; }
          .registros-subkpi-grid { grid-template-columns: 1fr 1fr !important; }
          .registros-subkpi-grid > :nth-child(3) { grid-column: 1 / -1; }
          .registro-subkpi-reteiva, .registro-estado-reteiva { display: none !important; }
          .registros-estados-grid { grid-template-columns: 1fr; overflow-x: visible; }
          .registro-estado-col { min-height: 0 !important; }
          .registros-integrada-layout { grid-template-columns: 1fr !important; }
          .filtros-grid { grid-template-columns: 1fr 1fr; row-gap: 10px; }
          .filtros-grid > :last-child { grid-column: 1 / -1; }
          .form-grid2   { grid-template-columns: 1fr; }
          .form-grid3   { grid-template-columns: 1fr 1fr; }
          .tabla-desktop{ display: none; }
          .cards-mobile { display: block; }
        }
        @media (min-width: 769px) and (max-width: 1040px) {
          .registros-integrada-layout { grid-template-columns: minmax(0, 1.25fr) minmax(340px, 0.75fr); }
          .registros-clientes-tabla th, .registros-clientes-tabla td { padding-left: 4px; padding-right: 4px; font-size: 10px; }
          .registros-empresa-resumen { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>
      {reciboAbierto && clienteResumenSeleccionado && (
        <ModalRecibo
          cliente={clienteResumenSeleccionado.cliente}
          items={clienteResumenSeleccionado.items
            .filter(r => reteivaSeleccionados.has(r.id) && !esFacturado(r))
            .sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)))
            .map(r => ({ id: r.id, fecha: r.fecha, servicio: nombreServicio(r), solicitante: r.solicitante || '', valor: parseFloat(r.valor) || 0, estado: r.estado }))}
          onClose={() => setReciboAbierto(false)}
        />
      )}
      {cambioCliente && (
        <div className="modal-overlay" style={{ zIndex: 1200 }}>
          <div className="modal" style={{ maxWidth: 460, padding: 22 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#08293f', marginBottom: 6 }}>Cambio de cliente</div>
            <div style={{ fontSize: 12, color: '#5a7a8a', marginBottom: 16, lineHeight: 1.5 }}>
              Cambiaste el cliente de <strong>{cambioCliente.de}</strong> a <strong>{cambioCliente.a}</strong>. ¿Qué quieres hacer?
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <button type="button" className="btn-ui btn-ui--md btn-ui--primary" style={{ height: 'auto', padding: '10px 14px', justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'normal' }} onClick={() => confirmarCambioCliente('solo')}>
                Mover solo este registro a {cambioCliente.a}
              </button>
              <button type="button" className="btn-ui btn-ui--md btn-ui--danger" style={{ height: 'auto', padding: '10px 14px', justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'normal' }}
                onClick={() => { if (window.confirm(`Esto renombrara TODOS los registros y equipos de "${cambioCliente.de}" a "${cambioCliente.a}". ¿Continuar?`)) confirmarCambioCliente('todos'); }}>
                Renombrar todo el cliente (corrige el nombre en todos sus registros y equipos)
              </button>
              <button type="button" className="btn-ui btn-ui--md btn-ui--outline" onClick={() => setCambioCliente(null)}>Cancelar, seguir editando</button>
            </div>
          </div>
        </div>
      )}
      <ModalPlanDesglose detalle={planDetalle} onClose={() => setPlanDetalle(null)} />
    </div>
  );
}
